import { rtkQueryBase as api } from '../rtk-query-base';
export const addTagTypes = [
  'App',
  'Events',
  'Programs',
  'Assets',
  'Campaigns',
  'Campaign-configuration',
  'Permissions',
  'Workflow Executions',
  'RefTags',
  'Workflows',
  'Distinct Values',
  'QuickSight',
  'Campaign Plans',
  'SonataApi',
  'SocialSignals',
  'PaidMediaDashboard',
  'Agent Sessions',
  'Publisher Entities',
  'Asset-rule-evaluation',
  'Brand',
  'Entity Metrics',
  'Docs',
  'issue-reports',
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      appControllerGetBase: build.query<
      AppControllerGetBaseApiResponse,
      AppControllerGetBaseApiArg
      >({
        query: () => ({ url: '/' }),
      }),
      appControllerGetMe: build.query<
      AppControllerGetMeApiResponse,
      AppControllerGetMeApiArg
      >({
        query: () => ({ url: '/me' }),
        providesTags: ['App'],
      }),
      appControllerValidateAlias: build.query<
      AppControllerValidateAliasApiResponse,
      AppControllerValidateAliasApiArg
      >({
        query: (queryArg) => ({ url: `/users/validate/${queryArg.alias}` }),
        providesTags: ['App'],
      }),
      appControllerGetFeatureFlags: build.query<
      AppControllerGetFeatureFlagsApiResponse,
      AppControllerGetFeatureFlagsApiArg
      >({
        query: () => ({ url: '/feature-flags' }),
        providesTags: ['App'],
      }),
      eventsControllerFindMany: build.query<
      EventsControllerFindManyApiResponse,
      EventsControllerFindManyApiArg
      >({
        query: (queryArg) => ({
          url: `/events/${queryArg.programId}`,
          params: { startTime: queryArg.startTime, endTime: queryArg.endTime },
        }),
        providesTags: ['Events'],
      }),
      programsControllerCreate: build.mutation<
      ProgramsControllerCreateApiResponse,
      ProgramsControllerCreateApiArg
      >({
        query: (queryArg) => ({
          url: '/programs',
          method: 'POST',
          body: queryArg.createProgramDto,
        }),
        invalidatesTags: ['Programs'],
      }),
      programsControllerFindMany: build.query<
      ProgramsControllerFindManyApiResponse,
      ProgramsControllerFindManyApiArg
      >({
        query: (queryArg) => ({
          url: '/programs',
          params: {
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            sort: queryArg.sort,
            filters: queryArg.filters,
          },
        }),
        providesTags: ['Programs'],
      }),
      programsControllerGet: build.query<
      ProgramsControllerGetApiResponse,
      ProgramsControllerGetApiArg
      >({
        query: (queryArg) => ({ url: `/programs/${queryArg.id}` }),
        providesTags: ['Programs'],
      }),
      programsControllerUpdate: build.mutation<
      ProgramsControllerUpdateApiResponse,
      ProgramsControllerUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/programs/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateProgramDto,
        }),
        invalidatesTags: ['Programs'],
      }),
      programsControllerDelete: build.mutation<
      ProgramsControllerDeleteApiResponse,
      ProgramsControllerDeleteApiArg
      >({
        query: (queryArg) => ({
          url: `/programs/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Programs'],
      }),
      assetsControllerCreate: build.mutation<
      AssetsControllerCreateApiResponse,
      AssetsControllerCreateApiArg
      >({
        query: (queryArg) => ({
          url: '/assets',
          method: 'POST',
          body: queryArg.createAssetsDto,
        }),
        invalidatesTags: ['Assets'],
      }),
      assetsControllerFindMany: build.query<
      AssetsControllerFindManyApiResponse,
      AssetsControllerFindManyApiArg
      >({
        query: (queryArg) => ({
          url: '/assets',
          params: {
            programId: queryArg.programId,
            campaignId: queryArg.campaignId,
            approvalStatus: queryArg.approvalStatus,
            assetId: queryArg.assetId,
            groupByAssetId: queryArg.groupByAssetId,
            productStrategyId: queryArg.productStrategyId,
            publisher: queryArg.publisher,
            country: queryArg.country,
            businessLine: queryArg.businessLine,
            size: queryArg.size,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            sort: queryArg.sort,
            filters: queryArg.filters,
          },
        }),
        providesTags: ['Assets'],
      }),
      assetsControllerUpdate: build.mutation<
      AssetsControllerUpdateApiResponse,
      AssetsControllerUpdateApiArg
      >({
        query: (queryArg) => ({
          url: '/assets',
          method: 'PUT',
          body: queryArg.updateAssetsDto,
        }),
        invalidatesTags: ['Assets'],
      }),
      assetsControllerDelete: build.mutation<
      AssetsControllerDeleteApiResponse,
      AssetsControllerDeleteApiArg
      >({
        query: (queryArg) => ({
          url: '/assets',
          method: 'DELETE',
          body: queryArg.deleteAssetsDto,
        }),
        invalidatesTags: ['Assets'],
      }),
      campaignsControllerFindMany: build.query<
      CampaignsControllerFindManyApiResponse,
      CampaignsControllerFindManyApiArg
      >({
        query: (queryArg) => ({
          url: '/campaigns',
          params: {
            includeArchived: queryArg.includeArchived,
            queue: queryArg.queue,
            updatedAtFrom: queryArg.updatedAtFrom,
            updatedAtTo: queryArg.updatedAtTo,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            sort: queryArg.sort,
            filters: queryArg.filters,
          },
        }),
        providesTags: ['Campaigns'],
      }),
      campaignsControllerGetQueueCounts: build.query<
      CampaignsControllerGetQueueCountsApiResponse,
      CampaignsControllerGetQueueCountsApiArg
      >({
        query: () => ({ url: '/campaigns/queue-counts' }),
        providesTags: ['Campaigns'],
      }),
      campaignsControllerRefresh: build.mutation<
      CampaignsControllerRefreshApiResponse,
      CampaignsControllerRefreshApiArg
      >({
        query: (queryArg) => ({
          url: `/campaigns/${queryArg.id}/refresh`,
          method: 'POST',
        }),
        invalidatesTags: ['Campaigns'],
      }),
      campaignsControllerCreate: build.mutation<
      CampaignsControllerCreateApiResponse,
      CampaignsControllerCreateApiArg
      >({
        query: (queryArg) => ({
          url: '/campaigns/desired',
          method: 'POST',
          body: queryArg.createCampaignDto,
        }),
        invalidatesTags: ['Campaigns'],
      }),
      campaignsControllerFindManyDesired: build.query<
      CampaignsControllerFindManyDesiredApiResponse,
      CampaignsControllerFindManyDesiredApiArg
      >({
        query: (queryArg) => ({
          url: '/campaigns/desired',
          params: {
            programId: queryArg.programId,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            sort: queryArg.sort,
            filters: queryArg.filters,
          },
        }),
        providesTags: ['Campaigns'],
      }),
      campaignsControllerGet: build.query<
      CampaignsControllerGetApiResponse,
      CampaignsControllerGetApiArg
      >({
        query: (queryArg) => ({ url: `/campaigns/desired/${queryArg.id}` }),
        providesTags: ['Campaigns'],
      }),
      campaignsControllerUpdate: build.mutation<
      CampaignsControllerUpdateApiResponse,
      CampaignsControllerUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/campaigns/desired/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateCampaignDto,
        }),
        invalidatesTags: ['Campaigns'],
      }),
      campaignsControllerDelete: build.mutation<
      CampaignsControllerDeleteApiResponse,
      CampaignsControllerDeleteApiArg
      >({
        query: (queryArg) => ({
          url: `/campaigns/desired/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Campaigns'],
      }),
      campaignsControllerGetPublished: build.query<
      CampaignsControllerGetPublishedApiResponse,
      CampaignsControllerGetPublishedApiArg
      >({
        query: (queryArg) => ({ url: `/campaigns/published/${queryArg.id}` }),
        providesTags: ['Campaigns'],
      }),
      campaignsControllerUpdatePublished: build.mutation<
      CampaignsControllerUpdatePublishedApiResponse,
      CampaignsControllerUpdatePublishedApiArg
      >({
        query: (queryArg) => ({
          url: `/campaigns/published/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateCampaignPublishedStateDto,
        }),
        invalidatesTags: ['Campaigns'],
      }),
      campaignsControllerValidateNotificationRecipients: build.mutation<
      CampaignsControllerValidateNotificationRecipientsApiResponse,
      CampaignsControllerValidateNotificationRecipientsApiArg
      >({
        query: (queryArg) => ({
          url: `/campaigns/${queryArg.id}/notification-recipients/validate`,
          method: 'POST',
          body: queryArg.validateNotificationRecipientsDto,
        }),
        invalidatesTags: ['Campaigns'],
      }),
      campaignsControllerUpdatePublisherCampaignConfig: build.mutation<
      CampaignsControllerUpdatePublisherCampaignConfigApiResponse,
      CampaignsControllerUpdatePublisherCampaignConfigApiArg
      >({
        query: (queryArg) => ({
          url: `/campaigns/${queryArg.id}/publisher-campaign-config`,
          method: 'PUT',
          body: queryArg.googlePublisherCampaignConfig,
        }),
        invalidatesTags: ['Campaigns'],
      }),
      campaignsControllerGetPublisherCampaignConfig: build.query<
      CampaignsControllerGetPublisherCampaignConfigApiResponse,
      CampaignsControllerGetPublisherCampaignConfigApiArg
      >({
        query: (queryArg) => ({
          url: `/campaigns/${queryArg.id}/publisher-campaign-config`,
        }),
        providesTags: ['Campaigns'],
      }),
      campaignConfigurationControllerCreate: build.mutation<
      CampaignConfigurationControllerCreateApiResponse,
      CampaignConfigurationControllerCreateApiArg
      >({
        query: (queryArg) => ({
          url: '/configs',
          method: 'POST',
          body: queryArg.createCampaignConfigurationDto,
        }),
        invalidatesTags: ['Campaign-configuration'],
      }),
      campaignConfigurationControllerFindMany: build.query<
      CampaignConfigurationControllerFindManyApiResponse,
      CampaignConfigurationControllerFindManyApiArg
      >({
        query: (queryArg) => ({
          url: '/configs',
          params: {
            source: queryArg.source,
            publisher: queryArg.publisher,
            country: queryArg.country,
            businessLine: queryArg.businessLine,
            search: queryArg.search,
            configType: queryArg.configType,
            owner: queryArg.owner,
            includeCampaignCount: queryArg.includeCampaignCount,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            sort: queryArg.sort,
            filters: queryArg.filters,
          },
        }),
        providesTags: ['Campaign-configuration'],
      }),
      campaignConfigurationControllerGet: build.query<
      CampaignConfigurationControllerGetApiResponse,
      CampaignConfigurationControllerGetApiArg
      >({
        query: (queryArg) => ({ url: `/configs/${queryArg.id}` }),
        providesTags: ['Campaign-configuration'],
      }),
      campaignConfigurationControllerUpdate: build.mutation<
      CampaignConfigurationControllerUpdateApiResponse,
      CampaignConfigurationControllerUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/configs/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateCampaignConfigurationDto,
        }),
        invalidatesTags: ['Campaign-configuration'],
      }),
      campaignConfigurationControllerDelete: build.mutation<
      CampaignConfigurationControllerDeleteApiResponse,
      CampaignConfigurationControllerDeleteApiArg
      >({
        query: (queryArg) => ({
          url: `/configs/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Campaign-configuration'],
      }),
      platformAdminControllerCreateBusinessUnit: build.mutation<
      PlatformAdminControllerCreateBusinessUnitApiResponse,
      PlatformAdminControllerCreateBusinessUnitApiArg
      >({
        query: (queryArg) => ({
          url: '/permissions/business-units',
          method: 'POST',
          body: queryArg.createBusinessUnitDto,
        }),
        invalidatesTags: ['Permissions'],
      }),
      platformAdminControllerFindBusinessUnits: build.query<
      PlatformAdminControllerFindBusinessUnitsApiResponse,
      PlatformAdminControllerFindBusinessUnitsApiArg
      >({
        query: (queryArg) => ({
          url: '/permissions/business-units',
          params: {
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            sort: queryArg.sort,
            filters: queryArg.filters,
          },
        }),
        providesTags: ['Permissions'],
      }),
      platformAdminControllerUpdateBusinessUnit: build.mutation<
      PlatformAdminControllerUpdateBusinessUnitApiResponse,
      PlatformAdminControllerUpdateBusinessUnitApiArg
      >({
        query: (queryArg) => ({
          url: `/permissions/business-units/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateBusinessUnitDto,
        }),
        invalidatesTags: ['Permissions'],
      }),
      platformAdminControllerDeleteBusinessUnit: build.mutation<
      PlatformAdminControllerDeleteBusinessUnitApiResponse,
      PlatformAdminControllerDeleteBusinessUnitApiArg
      >({
        query: (queryArg) => ({
          url: `/permissions/business-units/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Permissions'],
      }),
      myPermissionsControllerGetMyPermissions: build.query<
      MyPermissionsControllerGetMyPermissionsApiResponse,
      MyPermissionsControllerGetMyPermissionsApiArg
      >({
        query: () => ({ url: '/permissions/my-permissions' }),
        providesTags: ['Permissions'],
      }),
      businessUnitAdminControllerCreatePartner: build.mutation<
      BusinessUnitAdminControllerCreatePartnerApiResponse,
      BusinessUnitAdminControllerCreatePartnerApiArg
      >({
        query: (queryArg) => ({
          url: `/permissions/business-units/${queryArg.id}/partners`,
          method: 'POST',
          body: queryArg.createPartnerDto,
        }),
        invalidatesTags: ['Permissions'],
      }),
      businessUnitAdminControllerFindPartners: build.query<
      BusinessUnitAdminControllerFindPartnersApiResponse,
      BusinessUnitAdminControllerFindPartnersApiArg
      >({
        query: (queryArg) => ({
          url: `/permissions/business-units/${queryArg.id}/partners`,
          params: {
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            sort: queryArg.sort,
            filters: queryArg.filters,
          },
        }),
        providesTags: ['Permissions'],
      }),
      businessUnitAdminControllerFindBusinessUnitMembers: build.query<
      BusinessUnitAdminControllerFindBusinessUnitMembersApiResponse,
      BusinessUnitAdminControllerFindBusinessUnitMembersApiArg
      >({
        query: (queryArg) => ({
          url: `/permissions/business-units/${queryArg.id}/members`,
          params: {
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            sort: queryArg.sort,
            filters: queryArg.filters,
          },
        }),
        providesTags: ['Permissions'],
      }),
      businessUnitAdminControllerUpdatePartner: build.mutation<
      BusinessUnitAdminControllerUpdatePartnerApiResponse,
      BusinessUnitAdminControllerUpdatePartnerApiArg
      >({
        query: (queryArg) => ({
          url: `/permissions/partners/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updatePartnerDto,
        }),
        invalidatesTags: ['Permissions'],
      }),
      businessUnitAdminControllerCreateGroup: build.mutation<
      BusinessUnitAdminControllerCreateGroupApiResponse,
      BusinessUnitAdminControllerCreateGroupApiArg
      >({
        query: (queryArg) => ({
          url: '/permissions/groups',
          method: 'POST',
          body: queryArg.createGroupDto,
        }),
        invalidatesTags: ['Permissions'],
      }),
      businessUnitAdminControllerFindGroups: build.query<
      BusinessUnitAdminControllerFindGroupsApiResponse,
      BusinessUnitAdminControllerFindGroupsApiArg
      >({
        query: (queryArg) => ({
          url: '/permissions/groups',
          params: {
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            sort: queryArg.sort,
            filters: queryArg.filters,
            businessUnitId: queryArg.businessUnitId,
          },
        }),
        providesTags: ['Permissions'],
      }),
      businessUnitAdminControllerUpdateGroup: build.mutation<
      BusinessUnitAdminControllerUpdateGroupApiResponse,
      BusinessUnitAdminControllerUpdateGroupApiArg
      >({
        query: (queryArg) => ({
          url: `/permissions/groups/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateGroupDto,
        }),
        invalidatesTags: ['Permissions'],
      }),
      businessUnitAdminControllerUpdateGroupStatus: build.mutation<
      BusinessUnitAdminControllerUpdateGroupStatusApiResponse,
      BusinessUnitAdminControllerUpdateGroupStatusApiArg
      >({
        query: (queryArg) => ({
          url: `/permissions/groups/${queryArg.id}/status`,
          method: 'PUT',
          body: queryArg.updateGroupStatusDto,
        }),
        invalidatesTags: ['Permissions'],
      }),
      businessUnitAdminControllerAddMembership: build.mutation<
      BusinessUnitAdminControllerAddMembershipApiResponse,
      BusinessUnitAdminControllerAddMembershipApiArg
      >({
        query: (queryArg) => ({
          url: `/permissions/groups/${queryArg.id}/members`,
          method: 'POST',
          body: queryArg.createMembershipDto,
        }),
        invalidatesTags: ['Permissions'],
      }),
      businessUnitAdminControllerFindMembers: build.query<
      BusinessUnitAdminControllerFindMembersApiResponse,
      BusinessUnitAdminControllerFindMembersApiArg
      >({
        query: (queryArg) => ({
          url: `/permissions/groups/${queryArg.id}/members`,
          params: {
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            sort: queryArg.sort,
            filters: queryArg.filters,
          },
        }),
        providesTags: ['Permissions'],
      }),
      businessUnitAdminControllerBulkAddMemberships: build.mutation<
      BusinessUnitAdminControllerBulkAddMembershipsApiResponse,
      BusinessUnitAdminControllerBulkAddMembershipsApiArg
      >({
        query: (queryArg) => ({
          url: `/permissions/groups/${queryArg.id}/members/bulk`,
          method: 'POST',
          body: queryArg.bulkCreateMembershipsDto,
        }),
        invalidatesTags: ['Permissions'],
      }),
      businessUnitAdminControllerUpdateMembership: build.mutation<
      BusinessUnitAdminControllerUpdateMembershipApiResponse,
      BusinessUnitAdminControllerUpdateMembershipApiArg
      >({
        query: (queryArg) => ({
          url: `/permissions/members/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateMembershipDto,
        }),
        invalidatesTags: ['Permissions'],
      }),
      businessUnitAdminControllerRevokeMembership: build.mutation<
      BusinessUnitAdminControllerRevokeMembershipApiResponse,
      BusinessUnitAdminControllerRevokeMembershipApiArg
      >({
        query: (queryArg) => ({
          url: `/permissions/members/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Permissions'],
      }),
      workflowExecutionPlanControllerFindMany: build.query<
      WorkflowExecutionPlanControllerFindManyApiResponse,
      WorkflowExecutionPlanControllerFindManyApiArg
      >({
        query: (queryArg) => ({
          url: '/workflow-executions',
          params: {
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            sort: queryArg.sort,
            filters: queryArg.filters,
            campaignId: queryArg.campaignId,
            operationType: queryArg.operationType,
          },
        }),
        providesTags: ['Workflow Executions'],
      }),
      workflowExecutionPlanControllerFindOne: build.query<
      WorkflowExecutionPlanControllerFindOneApiResponse,
      WorkflowExecutionPlanControllerFindOneApiArg
      >({
        query: (queryArg) => ({ url: `/workflow-executions/${queryArg.id}` }),
        providesTags: ['Workflow Executions'],
      }),
      campaignTitlesControllerGetCampaignTitles: build.query<
      CampaignTitlesControllerGetCampaignTitlesApiResponse,
      CampaignTitlesControllerGetCampaignTitlesApiArg
      >({
        query: (queryArg) => ({
          url: '/campaign-titles',
          params: {
            gtiList: queryArg.gtiList,
            countryCode: queryArg.countryCode,
            locale: queryArg.locale,
            businessLine: queryArg.businessLine,
            pageId: queryArg.pageId,
            strategyId: queryArg.strategyId,
          },
        }),
      }),
      campaignTitlesControllerGetCampaignTitlesBatch: build.mutation<
      CampaignTitlesControllerGetCampaignTitlesBatchApiResponse,
      CampaignTitlesControllerGetCampaignTitlesBatchApiArg
      >({
        query: (queryArg) => ({
          url: '/campaign-titles',
          method: 'POST',
          body: queryArg.getCampaignTitlesRequest,
        }),
      }),
      refTagsControllerCreate: build.mutation<
      RefTagsControllerCreateApiResponse,
      RefTagsControllerCreateApiArg
      >({
        query: (queryArg) => ({
          url: '/reftags',
          method: 'POST',
          body: queryArg.createRefTagDto,
        }),
        invalidatesTags: ['RefTags'],
      }),
      refTagsControllerFindMany: build.query<
      RefTagsControllerFindManyApiResponse,
      RefTagsControllerFindManyApiArg
      >({
        query: (queryArg) => ({
          url: '/reftags',
          params: {
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            sort: queryArg.sort,
            filters: queryArg.filters,
            programId: queryArg.programId,
            assetId: queryArg.assetId,
            campaignId: queryArg.campaignId,
          },
        }),
        providesTags: ['RefTags'],
      }),
      refTagsControllerCreateBulk: build.mutation<
      RefTagsControllerCreateBulkApiResponse,
      RefTagsControllerCreateBulkApiArg
      >({
        query: (queryArg) => ({
          url: '/reftags/bulk',
          method: 'POST',
          body: queryArg.createRefTagsDto,
        }),
        invalidatesTags: ['RefTags'],
      }),
      refTagsControllerGet: build.query<
      RefTagsControllerGetApiResponse,
      RefTagsControllerGetApiArg
      >({
        query: (queryArg) => ({ url: `/reftags/${queryArg.id}` }),
        providesTags: ['RefTags'],
      }),
      refTagsControllerEmitAndesEvent: build.mutation<
      RefTagsControllerEmitAndesEventApiResponse,
      RefTagsControllerEmitAndesEventApiArg
      >({
        query: (queryArg) => ({
          url: `/reftags/andes/${queryArg.id}`,
          method: 'POST',
        }),
        invalidatesTags: ['RefTags'],
      }),
      workflowsControllerCreate: build.mutation<
      WorkflowsControllerCreateApiResponse,
      WorkflowsControllerCreateApiArg
      >({
        query: (queryArg) => ({
          url: '/workflows',
          method: 'POST',
          body: queryArg.createWorkflowDto,
        }),
        invalidatesTags: ['Workflows'],
      }),
      workflowsControllerFindMany: build.query<
      WorkflowsControllerFindManyApiResponse,
      WorkflowsControllerFindManyApiArg
      >({
        query: (queryArg) => ({
          url: '/workflows',
          params: {
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            sort: queryArg.sort,
            filters: queryArg.filters,
            programId: queryArg.programId,
            campaignId: queryArg.campaignId,
          },
        }),
        providesTags: ['Workflows'],
      }),
      workflowsControllerFindQueuedProgramsWithCampaigns: build.query<
      WorkflowsControllerFindQueuedProgramsWithCampaignsApiResponse,
      WorkflowsControllerFindQueuedProgramsWithCampaignsApiArg
      >({
        query: () => ({ url: '/workflows/queued-programs-with-campaigns' }),
        providesTags: ['Workflows'],
      }),
      workflowsControllerFindNextQueuedWorkflows: build.query<
      WorkflowsControllerFindNextQueuedWorkflowsApiResponse,
      WorkflowsControllerFindNextQueuedWorkflowsApiArg
      >({
        query: (queryArg) => ({
          url: '/workflows/next-queued',
          params: {
            limit: queryArg.limit,
            inProgressSince: queryArg.inProgressSince,
          },
        }),
        providesTags: ['Workflows'],
      }),
      workflowsControllerFindOne: build.query<
      WorkflowsControllerFindOneApiResponse,
      WorkflowsControllerFindOneApiArg
      >({
        query: (queryArg) => ({ url: `/workflows/${queryArg.id}` }),
        providesTags: ['Workflows'],
      }),
      workflowsControllerUpdate: build.mutation<
      WorkflowsControllerUpdateApiResponse,
      WorkflowsControllerUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/workflows/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateWorkflowDto,
        }),
        invalidatesTags: ['Workflows'],
      }),
      workflowsControllerDelete: build.mutation<
      WorkflowsControllerDeleteApiResponse,
      WorkflowsControllerDeleteApiArg
      >({
        query: (queryArg) => ({
          url: `/workflows/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Workflows'],
      }),
      distinctValuesControllerGetCampaignOptions: build.query<
      DistinctValuesControllerGetCampaignOptionsApiResponse,
      DistinctValuesControllerGetCampaignOptionsApiArg
      >({
        query: () => ({ url: '/distinct-values/campaigns' }),
        providesTags: ['Distinct Values'],
      }),
      distinctValuesControllerGetDistinctTitles: build.query<
      DistinctValuesControllerGetDistinctTitlesApiResponse,
      DistinctValuesControllerGetDistinctTitlesApiArg
      >({
        query: (queryArg) => ({
          url: '/distinct-values/titles',
          params: {
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            country: queryArg.country,
            locale: queryArg.locale,
            search: queryArg.search,
            sort: queryArg.sort,
            campaignIds: queryArg.campaignIds,
            gti: queryArg.gti,
            hasActiveCampaigns: queryArg.hasActiveCampaigns,
            publisher: queryArg.publisher,
          },
        }),
        providesTags: ['Distinct Values'],
      }),
      distinctValuesControllerGetDistinctValues: build.query<
      DistinctValuesControllerGetDistinctValuesApiResponse,
      DistinctValuesControllerGetDistinctValuesApiArg
      >({
        query: (queryArg) => ({
          url: '/distinct-values',
          params: {
            entityType: queryArg.entityType,
            field: queryArg.field,
            programId: queryArg.programId,
            filters: queryArg.filters,
          },
        }),
        providesTags: ['Distinct Values'],
      }),
      groupTitlesControllerGroupTitles: build.mutation<
      GroupTitlesControllerGroupTitlesApiResponse,
      GroupTitlesControllerGroupTitlesApiArg
      >({
        query: (queryArg) => ({
          url: '/group-titles',
          method: 'POST',
          body: queryArg.groupTitlesRequest,
        }),
      }),
      quicksightControllerGenerateTiktokVtaDashboardEmbedUrl: build.mutation<
      QuicksightControllerGenerateTiktokVtaDashboardEmbedUrlApiResponse,
      QuicksightControllerGenerateTiktokVtaDashboardEmbedUrlApiArg
      >({
        query: () => ({
          url: '/quicksight/generate-tiktok-vta-dashboard-embed-url',
          method: 'POST',
        }),
        invalidatesTags: ['QuickSight'],
      }),
      campaignPlansControllerCreate: build.mutation<
      CampaignPlansControllerCreateApiResponse,
      CampaignPlansControllerCreateApiArg
      >({
        query: (queryArg) => ({
          url: '/campaign-plans',
          method: 'POST',
          body: queryArg.createCampaignPlanDto,
        }),
        invalidatesTags: ['Campaign Plans'],
      }),
      campaignPlansControllerFindByCampaignId: build.query<
      CampaignPlansControllerFindByCampaignIdApiResponse,
      CampaignPlansControllerFindByCampaignIdApiArg
      >({
        query: (queryArg) => ({
          url: `/campaign-plans/campaign/${queryArg.campaignId}`,
        }),
        providesTags: ['Campaign Plans'],
      }),
      campaignPlansControllerFindAllByCampaignId: build.query<
      CampaignPlansControllerFindAllByCampaignIdApiResponse,
      CampaignPlansControllerFindAllByCampaignIdApiArg
      >({
        query: (queryArg) => ({
          url: `/campaign-plans/campaign/${queryArg.campaignId}/all`,
          params: { status: queryArg.status },
        }),
        providesTags: ['Campaign Plans'],
      }),
      campaignPlansControllerFindOne: build.query<
      CampaignPlansControllerFindOneApiResponse,
      CampaignPlansControllerFindOneApiArg
      >({
        query: (queryArg) => ({ url: `/campaign-plans/${queryArg.id}` }),
        providesTags: ['Campaign Plans'],
      }),
      campaignPlansControllerUpdate: build.mutation<
      CampaignPlansControllerUpdateApiResponse,
      CampaignPlansControllerUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/campaign-plans/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateCampaignPlanDto,
        }),
        invalidatesTags: ['Campaign Plans'],
      }),
      campaignPlansControllerUpdateStatus: build.mutation<
      CampaignPlansControllerUpdateStatusApiResponse,
      CampaignPlansControllerUpdateStatusApiArg
      >({
        query: (queryArg) => ({
          url: `/campaign-plans/${queryArg.id}/status`,
          method: 'POST',
          body: queryArg.updateStatusDto,
        }),
        invalidatesTags: ['Campaign Plans'],
      }),
      sonataApiControllerSearchPageIds: build.query<
      SonataApiControllerSearchPageIdsApiResponse,
      SonataApiControllerSearchPageIdsApiArg
      >({
        query: (queryArg) => ({
          url: '/sonata-api/pageIds',
          params: { pageType: queryArg.pageType },
        }),
        providesTags: ['SonataApi'],
      }),
      catalogControllerGetCatalogTitles: build.mutation<
      CatalogControllerGetCatalogTitlesApiResponse,
      CatalogControllerGetCatalogTitlesApiArg
      >({
        query: (queryArg) => ({
          url: '/catalog/titles',
          method: 'POST',
          body: queryArg.getCatalogGtiRequest,
        }),
      }),
      socialSignalsControllerGetRecommendations: build.query<
      SocialSignalsControllerGetRecommendationsApiResponse,
      SocialSignalsControllerGetRecommendationsApiArg
      >({
        query: (queryArg) => ({
          url: '/social-signals/recommendations',
          params: {
            country: queryArg.country,
            locale: queryArg.locale,
            riskLabel: queryArg.riskLabel,
            sentiment: queryArg.sentiment,
            sourcePlatform: queryArg.sourcePlatform,
            trendSearch: queryArg.trendSearch,
            startDate: queryArg.startDate,
            endDate: queryArg.endDate,
            topic: queryArg.topic,
            sortBy: queryArg.sortBy,
            sortDirection: queryArg.sortDirection,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
          },
        }),
        providesTags: ['SocialSignals'],
      }),
      paidMediaDashboardControllerGetInsights: build.mutation<
      PaidMediaDashboardControllerGetInsightsApiResponse,
      PaidMediaDashboardControllerGetInsightsApiArg
      >({
        query: (queryArg) => ({
          url: '/paid-media-dashboard/insights',
          method: 'POST',
          body: queryArg.paidMediaInsightsQueryDto,
        }),
        invalidatesTags: ['PaidMediaDashboard'],
      }),
      paidMediaDashboardControllerQuery: build.mutation<
      PaidMediaDashboardControllerQueryApiResponse,
      PaidMediaDashboardControllerQueryApiArg
      >({
        query: (queryArg) => ({
          url: '/paid-media-dashboard/query',
          method: 'POST',
          body: queryArg.paidMediaDashboardQueryDto,
        }),
        invalidatesTags: ['PaidMediaDashboard'],
      }),
      paidMediaDashboardControllerStartExport: build.mutation<
      PaidMediaDashboardControllerStartExportApiResponse,
      PaidMediaDashboardControllerStartExportApiArg
      >({
        query: (queryArg) => ({
          url: '/paid-media-dashboard/export',
          method: 'POST',
          body: queryArg.paidMediaDashboardExportDto,
        }),
        invalidatesTags: ['PaidMediaDashboard'],
      }),
      paidMediaDashboardControllerGetExportStatus: build.query<
      PaidMediaDashboardControllerGetExportStatusApiResponse,
      PaidMediaDashboardControllerGetExportStatusApiArg
      >({
        query: (queryArg) => ({
          url: `/paid-media-dashboard/export/${queryArg.exportId}`,
        }),
        providesTags: ['PaidMediaDashboard'],
      }),
      agentSessionsControllerCreate: build.mutation<
      AgentSessionsControllerCreateApiResponse,
      AgentSessionsControllerCreateApiArg
      >({
        query: (queryArg) => ({
          url: '/agent-sessions',
          method: 'POST',
          body: queryArg.createAgentSessionDto,
        }),
        invalidatesTags: ['Agent Sessions'],
      }),
      agentSessionsControllerFindMany: build.query<
      AgentSessionsControllerFindManyApiResponse,
      AgentSessionsControllerFindManyApiArg
      >({
        query: (queryArg) => ({
          url: '/agent-sessions',
          params: {
            entityId: queryArg.entityId,
            entityType: queryArg.entityType,
            userId: queryArg.userId,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            sort: queryArg.sort,
          },
        }),
        providesTags: ['Agent Sessions'],
      }),
      agentSessionsControllerFindOne: build.query<
      AgentSessionsControllerFindOneApiResponse,
      AgentSessionsControllerFindOneApiArg
      >({
        query: (queryArg) => ({ url: `/agent-sessions/${queryArg.sessionId}` }),
        providesTags: ['Agent Sessions'],
      }),
      agentSessionsControllerUpdate: build.mutation<
      AgentSessionsControllerUpdateApiResponse,
      AgentSessionsControllerUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/agent-sessions/${queryArg.sessionId}`,
          method: 'PUT',
          body: queryArg.updateAgentSessionDto,
        }),
        invalidatesTags: ['Agent Sessions'],
      }),
      agentSessionsControllerRemove: build.mutation<
      AgentSessionsControllerRemoveApiResponse,
      AgentSessionsControllerRemoveApiArg
      >({
        query: (queryArg) => ({
          url: `/agent-sessions/${queryArg.sessionId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Agent Sessions'],
      }),
      publisherEntitiesControllerListForCampaign: build.query<
      PublisherEntitiesControllerListForCampaignApiResponse,
      PublisherEntitiesControllerListForCampaignApiArg
      >({
        query: (queryArg) => ({
          url: `/publisher-entities/campaign/${queryArg.campaignId}`,
          params: {
            pageSize: queryArg.pageSize,
            sort: queryArg.sort,
            publisher: queryArg.publisher,
            publisherEntityType: queryArg.publisherEntityType,
            entityStatus: queryArg.entityStatus,
            syncStatus: queryArg.syncStatus,
            parentSourceEntityId: queryArg.parentSourceEntityId,
            parentSourceEntityIds: queryArg.parentSourceEntityIds,
            parentEntityIds: queryArg.parentEntityIds,
            parentPublisherEntityId: queryArg.parentPublisherEntityId,
            page: queryArg.page,
          },
        }),
        providesTags: ['Publisher Entities'],
      }),
      assetRuleEvaluationControllerEvictByRuleId: build.mutation<
      AssetRuleEvaluationControllerEvictByRuleIdApiResponse,
      AssetRuleEvaluationControllerEvictByRuleIdApiArg
      >({
        query: (queryArg) => ({
          url: `/asset-rule-evaluations/rule/${queryArg.ruleId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Asset-rule-evaluation'],
      }),
      assetRuleEvaluationControllerTriggerAdhoc: build.mutation<
      AssetRuleEvaluationControllerTriggerAdhocApiResponse,
      AssetRuleEvaluationControllerTriggerAdhocApiArg
      >({
        query: (queryArg) => ({
          url: '/asset-rule-evaluations/adhoc',
          method: 'POST',
          body: queryArg.adhocEvaluationDto,
        }),
        invalidatesTags: ['Asset-rule-evaluation'],
      }),
      assetRuleEvaluationControllerFindByAssetId: build.query<
      AssetRuleEvaluationControllerFindByAssetIdApiResponse,
      AssetRuleEvaluationControllerFindByAssetIdApiArg
      >({
        query: (queryArg) => ({
          url: `/asset-rule-evaluations/${queryArg.assetId}`,
          params: { ruleId: queryArg.ruleId },
        }),
        providesTags: ['Asset-rule-evaluation'],
      }),
      assetRuleEvaluationControllerEvictByAssetId: build.mutation<
      AssetRuleEvaluationControllerEvictByAssetIdApiResponse,
      AssetRuleEvaluationControllerEvictByAssetIdApiArg
      >({
        query: (queryArg) => ({
          url: `/asset-rule-evaluations/${queryArg.assetId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Asset-rule-evaluation'],
      }),
      brandControllerGetAvailablePresets: build.query<
      BrandControllerGetAvailablePresetsApiResponse,
      BrandControllerGetAvailablePresetsApiArg
      >({
        query: () => ({ url: '/brand/presets' }),
        providesTags: ['Brand'],
      }),
      entityMetricsControllerGetFields: build.query<
      EntityMetricsControllerGetFieldsApiResponse,
      EntityMetricsControllerGetFieldsApiArg
      >({
        query: (queryArg) => ({
          url: '/metrics/entities/allowed-fields',
          params: { entity: queryArg.entity },
        }),
        providesTags: ['Entity Metrics'],
      }),
      entityMetricsControllerQuery: build.mutation<
      EntityMetricsControllerQueryApiResponse,
      EntityMetricsControllerQueryApiArg
      >({
        query: (queryArg) => ({
          url: '/metrics/entities',
          method: 'POST',
          body: queryArg.entityMetricsQueryDto,
        }),
        invalidatesTags: ['Entity Metrics'],
      }),
      docsControllerListPages: build.query<
      DocsControllerListPagesApiResponse,
      DocsControllerListPagesApiArg
      >({
        query: (queryArg) => ({ url: `/docs/${queryArg.app}` }),
        providesTags: ['Docs'],
      }),
      docsControllerSearch: build.query<
      DocsControllerSearchApiResponse,
      DocsControllerSearchApiArg
      >({
        query: (queryArg) => ({
          url: `/docs/${queryArg.app}/search`,
          params: { q: queryArg.q },
        }),
        providesTags: ['Docs'],
      }),
      docsControllerGetPage: build.query<
      DocsControllerGetPageApiResponse,
      DocsControllerGetPageApiArg
      >({
        query: (queryArg) => ({
          url: `/docs/${queryArg.app}/${queryArg.folder}/${queryArg.page}`,
        }),
        providesTags: ['Docs'],
      }),
      issueReportsControllerCreate: build.mutation<
      IssueReportsControllerCreateApiResponse,
      IssueReportsControllerCreateApiArg
      >({
        query: (queryArg) => ({
          url: '/issue-reports',
          method: 'POST',
          body: queryArg.createIssueReportDto,
        }),
        invalidatesTags: ['issue-reports'],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as leelaApiGenerated };
export type AppControllerGetBaseApiResponse = /** status 200  */ object;
export type AppControllerGetBaseApiArg = void;
export type AppControllerGetMeApiResponse = /** status 200  */ UserContextDto;
export type AppControllerGetMeApiArg = void;
export type AppControllerValidateAliasApiResponse = unknown;
export interface AppControllerValidateAliasApiArg {
  /** Amazon user alias to validate */
  alias: string;
}
export type AppControllerGetFeatureFlagsApiResponse = /** status 200  */ object;
export type AppControllerGetFeatureFlagsApiArg = void;
export type EventsControllerFindManyApiResponse =
  /** status 200  */ AuditEvent[];
export interface EventsControllerFindManyApiArg {
  programId: string;
  startTime?: string;
  endTime?: string;
}
export type ProgramsControllerCreateApiResponse = /** status 201  */ ProgramDto;
export interface ProgramsControllerCreateApiArg {
  createProgramDto: CreateProgramDto;
}
export type ProgramsControllerFindManyApiResponse =
  /** status 200  */ PaginatedDocumented & {
    data?: ProgramDto[];
  };
export interface ProgramsControllerFindManyApiArg {
  page?: number;
  pageSize?: number;
  /** Parameter to sort by.
          <p>To sort by multiple fields, provide params as an array. The order in url defines an order of sorting</p>
          <p>
                 <b>Format: </b> sort=[fieldName:direction] where direction is -1 (descending) or 1 (ascending)
              </p>
          <p>
                 <b>Example: </b> name:1
              </p>
          <p>
                 <b>Default Value: </b> sort=[createdAt:-1]
              </p>
           */
  sort?: string[];
  /** Filter by a field in the data..
              <p>
                 <b>Format: </b> filters[fieldName]=VALUE
              </p>
              <p>
                 <b>Example 1: in: </b> filters[fieldName][$in]=VALUE_1&filters[fieldName][$in]=VALUE_2
              </p>
              <p>
                 <b>Example 2: not equal: </b> filters[fieldName][$ne]=VALUE
              </p>
              <h4>Available Operations</h4><ul><li>$eq - equal</li>
    <li>$ne - not equal to</li>
    <li>$gt - greater than</li>
    <li>$gte - greater than or equal to</li>
    <li>$lte - less than or equal to</li>
    <li>$lt - less than</li>
    <li>$in - is in array</li>
    <li>$nin - is not in array</li></ul> */
  filters?: object;
}
export type ProgramsControllerGetApiResponse = /** status 200  */ ProgramDto;
export interface ProgramsControllerGetApiArg {
  id: string;
}
export type ProgramsControllerUpdateApiResponse = /** status 200  */ ProgramDto;
export interface ProgramsControllerUpdateApiArg {
  id: string;
  updateProgramDto: UpdateProgramDto;
}
export type ProgramsControllerDeleteApiResponse = unknown;
export interface ProgramsControllerDeleteApiArg {
  id: string;
}
export type AssetsControllerCreateApiResponse = /** status 201  */ AssetDto[];
export interface AssetsControllerCreateApiArg {
  createAssetsDto: CreateAssetsDto;
}
export type AssetsControllerFindManyApiResponse =
  /** status 200  */ PaginatedDocumented & {
    data?: AssetDto[];
  };
export interface AssetsControllerFindManyApiArg {
  programId?: string;
  campaignId?: string;
  approvalStatus?: 'APPROVED' | 'PENDING' | 'REJECTED' | 'BLOCKED';
  assetId?: string[];
  groupByAssetId?: boolean;
  productStrategyId?: string;
  publisher?: string;
  country?: string;
  businessLine?:
  | 'SVOD'
  | 'TVOD'
  | 'CHANNEL'
  | 'Sports SVOD'
  | 'Sports TVOD'
  | 'Sports Channel';
  size?: string;
  page?: number;
  pageSize?: number;
  /** Parameter to sort by.
          <p>To sort by multiple fields, provide params as an array. The order in url defines an order of sorting</p>
          <p>
                 <b>Format: </b> sort=[fieldName:direction] where direction is -1 (descending) or 1 (ascending)
              </p>
          <p>
                 <b>Example: </b> name:1
              </p>
          <p>
                 <b>Default Value: </b> sort=[createdAt:-1]
              </p>
           */
  sort?: string[];
  /** Filter by a field in the data..
              <p>
                 <b>Format: </b> filters[fieldName]=VALUE
              </p>
              <p>
                 <b>Example 1: in: </b> filters[fieldName][$in]=VALUE_1&filters[fieldName][$in]=VALUE_2
              </p>
              <p>
                 <b>Example 2: not equal: </b> filters[fieldName][$ne]=VALUE
              </p>
              <h4>Available Operations</h4><ul><li>$eq - equal</li>
    <li>$ne - not equal to</li>
    <li>$gt - greater than</li>
    <li>$gte - greater than or equal to</li>
    <li>$lte - less than or equal to</li>
    <li>$lt - less than</li>
    <li>$in - is in array</li>
    <li>$nin - is not in array</li></ul> */
  filters?: object;
}
export type AssetsControllerUpdateApiResponse = /** status 200  */ AssetDto[];
export interface AssetsControllerUpdateApiArg {
  updateAssetsDto: UpdateAssetsDto;
}
export type AssetsControllerDeleteApiResponse = unknown;
export interface AssetsControllerDeleteApiArg {
  deleteAssetsDto: DeleteAssetsDto;
}
export type CampaignsControllerFindManyApiResponse =
  /** status 200  */ PaginatedDocumented & {
    data?: CampaignListItemDto[];
  };
export interface CampaignsControllerFindManyApiArg {
  /** Include archived campaigns in the response */
  includeArchived?: boolean;
  queue?: CampaignQueue;
  updatedAtFrom?: string;
  updatedAtTo?: string;
  page?: number;
  pageSize?: number;
  /** Parameter to sort by.
          <p>To sort by multiple fields, provide params as an array. The order in url defines an order of sorting</p>
          <p>
                 <b>Format: </b> sort=[fieldName:direction] where direction is -1 (descending) or 1 (ascending)
              </p>
          <p>
                 <b>Example: </b> name:1
              </p>
          <p>
                 <b>Default Value: </b> sort=[createdAt:-1]
              </p>
           */
  sort?: string[];
  /** Filter by a field in the data..
              <p>
                 <b>Format: </b> filters[fieldName]=VALUE
              </p>
              <p>
                 <b>Example 1: in: </b> filters[fieldName][$in]=VALUE_1&filters[fieldName][$in]=VALUE_2
              </p>
              <p>
                 <b>Example 2: not equal: </b> filters[fieldName][$ne]=VALUE
              </p>
              <h4>Available Operations</h4><ul><li>$eq - equal</li>
    <li>$ne - not equal to</li>
    <li>$gt - greater than</li>
    <li>$gte - greater than or equal to</li>
    <li>$lte - less than or equal to</li>
    <li>$lt - less than</li>
    <li>$in - is in array</li>
    <li>$nin - is not in array</li></ul> */
  filters?: object;
}
export type CampaignsControllerGetQueueCountsApiResponse =
  /** status 200  */ CampaignQueueCountsDto;
export type CampaignsControllerGetQueueCountsApiArg = void;
export type CampaignsControllerRefreshApiResponse = unknown;
export interface CampaignsControllerRefreshApiArg {
  id: string;
}
export type CampaignsControllerCreateApiResponse =
  /** status 201  */ CampaignDesiredStateDto;
export interface CampaignsControllerCreateApiArg {
  createCampaignDto: CreateCampaignDto;
}
export type CampaignsControllerFindManyDesiredApiResponse =
  /** status 200  */ PaginatedDocumented & {
    data?: CampaignDesiredStateDto[];
  };
export interface CampaignsControllerFindManyDesiredApiArg {
  programId: string;
  page?: number;
  pageSize?: number;
  /** Parameter to sort by.
          <p>To sort by multiple fields, provide params as an array. The order in url defines an order of sorting</p>
          <p>
                 <b>Format: </b> sort=[fieldName:direction] where direction is -1 (descending) or 1 (ascending)
              </p>
          <p>
                 <b>Example: </b> name:1
              </p>
          <p>
                 <b>Default Value: </b> sort=[createdAt:-1]
              </p>
           */
  sort?: string[];
  /** Filter by a field in the data..
              <p>
                 <b>Format: </b> filters[fieldName]=VALUE
              </p>
              <p>
                 <b>Example 1: in: </b> filters[fieldName][$in]=VALUE_1&filters[fieldName][$in]=VALUE_2
              </p>
              <p>
                 <b>Example 2: not equal: </b> filters[fieldName][$ne]=VALUE
              </p>
              <h4>Available Operations</h4><ul><li>$eq - equal</li>
    <li>$ne - not equal to</li>
    <li>$gt - greater than</li>
    <li>$gte - greater than or equal to</li>
    <li>$lte - less than or equal to</li>
    <li>$lt - less than</li>
    <li>$in - is in array</li>
    <li>$nin - is not in array</li></ul> */
  filters?: object;
}
export type CampaignsControllerGetApiResponse =
  /** status 200  */ CampaignDesiredStateDto;
export interface CampaignsControllerGetApiArg {
  id: string;
}
export type CampaignsControllerUpdateApiResponse =
  /** status 200  */ CampaignDesiredStateDto;
export interface CampaignsControllerUpdateApiArg {
  id: string;
  updateCampaignDto: UpdateCampaignDto;
}
export type CampaignsControllerDeleteApiResponse = unknown;
export interface CampaignsControllerDeleteApiArg {
  id: string;
}
export type CampaignsControllerGetPublishedApiResponse =
  /** status 200  */ CampaignPublishedStateDto;
export interface CampaignsControllerGetPublishedApiArg {
  id: string;
}
export type CampaignsControllerUpdatePublishedApiResponse =
  /** status 200  */ CampaignPublishedStateDto;
export interface CampaignsControllerUpdatePublishedApiArg {
  id: string;
  updateCampaignPublishedStateDto: UpdateCampaignPublishedStateDto;
}
export type CampaignsControllerValidateNotificationRecipientsApiResponse =
  /** status 201  */ NotificationRecipientEligibilityDto;
export interface CampaignsControllerValidateNotificationRecipientsApiArg {
  id: string;
  validateNotificationRecipientsDto: ValidateNotificationRecipientsDto;
}
export type CampaignsControllerUpdatePublisherCampaignConfigApiResponse =
  /** status 200 Publisher campaign config with execution-populated fields */ GooglePublisherCampaignConfig;
export interface CampaignsControllerUpdatePublisherCampaignConfigApiArg {
  id: string;
  googlePublisherCampaignConfig: GooglePublisherCampaignConfig;
}
export type CampaignsControllerGetPublisherCampaignConfigApiResponse =
  /** status 200  */ GooglePublisherCampaignConfig;
export interface CampaignsControllerGetPublisherCampaignConfigApiArg {
  id: string;
}
export type CampaignConfigurationControllerCreateApiResponse =
  /** status 201  */ CampaignConfigurationDto;
export interface CampaignConfigurationControllerCreateApiArg {
  createCampaignConfigurationDto: CreateCampaignConfigurationDto;
}
export type CampaignConfigurationControllerFindManyApiResponse =
  /** status 200  */ PaginatedDocumented & {
    data?: CampaignConfigurationDto[];
  };
export interface CampaignConfigurationControllerFindManyApiArg {
  source?: CreationSource;
  /** Filter by supportedPublishers */
  publisher?: Publisher;
  /** Filter by supportedCountries */
  country?: Country;
  /** Filter by supportedBusinessLines */
  businessLine?: BusinessLine;
  /** Case-insensitive substring search on config name */
  search?: string;
  configType?: 'GTI_BLOCKLIST' | 'PUBLISHER_ACCOUNT' | 'CONTENT_RULE';
  owner?: string;
  /** Include campaign count in response (default: true) */
  includeCampaignCount?: boolean;
  page?: number;
  pageSize?: number;
  /** Parameter to sort by.
          <p>To sort by multiple fields, provide params as an array. The order in url defines an order of sorting</p>
          <p>
                 <b>Format: </b> sort=[fieldName:direction] where direction is -1 (descending) or 1 (ascending)
              </p>
          <p>
                 <b>Example: </b> name:1
              </p>
          <p>
                 <b>Default Value: </b> sort=[updatedAt:-1]
              </p>
           */
  sort?: string[];
  /** Filter by a field in the data..
              <p>
                 <b>Format: </b> filters[fieldName]=VALUE
              </p>
              <p>
                 <b>Example 1: in: </b> filters[fieldName][$in]=VALUE_1&filters[fieldName][$in]=VALUE_2
              </p>
              <p>
                 <b>Example 2: not equal: </b> filters[fieldName][$ne]=VALUE
              </p>
              <h4>Available Operations</h4><ul><li>$eq - equal</li>
    <li>$ne - not equal to</li>
    <li>$gt - greater than</li>
    <li>$gte - greater than or equal to</li>
    <li>$lte - less than or equal to</li>
    <li>$lt - less than</li>
    <li>$in - is in array</li>
    <li>$nin - is not in array</li></ul> */
  filters?: object;
}
export type CampaignConfigurationControllerGetApiResponse =
  /** status 200  */ CampaignConfigurationDto;
export interface CampaignConfigurationControllerGetApiArg {
  id: string;
}
export type CampaignConfigurationControllerUpdateApiResponse =
  /** status 200  */ CampaignConfigurationDto;
export interface CampaignConfigurationControllerUpdateApiArg {
  id: string;
  updateCampaignConfigurationDto: UpdateCampaignConfigurationDto;
}
export type CampaignConfigurationControllerDeleteApiResponse = unknown;
export interface CampaignConfigurationControllerDeleteApiArg {
  id: string;
}
export type PlatformAdminControllerCreateBusinessUnitApiResponse =
  /** status 201  */ BusinessUnitDto;
export interface PlatformAdminControllerCreateBusinessUnitApiArg {
  createBusinessUnitDto: CreateBusinessUnitDto;
}
export type PlatformAdminControllerFindBusinessUnitsApiResponse =
  /** status 200  */ PaginatedDocumented & {
    data?: BusinessUnitDto[];
  };
export interface PlatformAdminControllerFindBusinessUnitsApiArg {
  page?: number;
  pageSize?: number;
  /** Parameter to sort by.
          <p>To sort by multiple fields, provide params as an array. The order in url defines an order of sorting</p>
          <p>
                 <b>Format: </b> sort=[fieldName:direction] where direction is -1 (descending) or 1 (ascending)
              </p>
          <p>
                 <b>Example: </b> name:1
              </p>
          <p>
                 <b>Default Value: </b> sort=[createdAt:-1]
              </p>
           */
  sort?: string[];
  /** Filter by a field in the data..
              <p>
                 <b>Format: </b> filters[fieldName]=VALUE
              </p>
              <p>
                 <b>Example 1: in: </b> filters[fieldName][$in]=VALUE_1&filters[fieldName][$in]=VALUE_2
              </p>
              <p>
                 <b>Example 2: not equal: </b> filters[fieldName][$ne]=VALUE
              </p>
              <h4>Available Operations</h4><ul><li>$eq - equal</li>
    <li>$ne - not equal to</li>
    <li>$gt - greater than</li>
    <li>$gte - greater than or equal to</li>
    <li>$lte - less than or equal to</li>
    <li>$lt - less than</li>
    <li>$in - is in array</li>
    <li>$nin - is not in array</li></ul> */
  filters?: object;
}
export type PlatformAdminControllerUpdateBusinessUnitApiResponse =
  /** status 200  */ BusinessUnitDto;
export interface PlatformAdminControllerUpdateBusinessUnitApiArg {
  id: string;
  updateBusinessUnitDto: UpdateBusinessUnitDto;
}
export type PlatformAdminControllerDeleteBusinessUnitApiResponse =
  /** status 200  */ object;
export interface PlatformAdminControllerDeleteBusinessUnitApiArg {
  id: string;
}
export type MyPermissionsControllerGetMyPermissionsApiResponse =
  /** status 200  */ MyPermissionsResponseDto;
export type MyPermissionsControllerGetMyPermissionsApiArg = void;
export type BusinessUnitAdminControllerCreatePartnerApiResponse =
  /** status 201  */ PartnerDto;
export interface BusinessUnitAdminControllerCreatePartnerApiArg {
  id: string;
  createPartnerDto: CreatePartnerDto;
}
export type BusinessUnitAdminControllerFindPartnersApiResponse =
  /** status 200  */ PaginatedDocumented & {
    data?: PartnerDto[];
  };
export interface BusinessUnitAdminControllerFindPartnersApiArg {
  id: string;
  page?: number;
  pageSize?: number;
  /** Parameter to sort by.
          <p>To sort by multiple fields, provide params as an array. The order in url defines an order of sorting</p>
          <p>
                 <b>Format: </b> sort=[fieldName:direction] where direction is -1 (descending) or 1 (ascending)
              </p>
          <p>
                 <b>Example: </b> name:1
              </p>
          <p>
                 <b>Default Value: </b> sort=[createdAt:-1]
              </p>
           */
  sort?: string[];
  /** Filter by a field in the data..
              <p>
                 <b>Format: </b> filters[fieldName]=VALUE
              </p>
              <p>
                 <b>Example 1: in: </b> filters[fieldName][$in]=VALUE_1&filters[fieldName][$in]=VALUE_2
              </p>
              <p>
                 <b>Example 2: not equal: </b> filters[fieldName][$ne]=VALUE
              </p>
              <h4>Available Operations</h4><ul><li>$eq - equal</li>
    <li>$ne - not equal to</li>
    <li>$gt - greater than</li>
    <li>$gte - greater than or equal to</li>
    <li>$lte - less than or equal to</li>
    <li>$lt - less than</li>
    <li>$in - is in array</li>
    <li>$nin - is not in array</li></ul> */
  filters?: object;
}
export type BusinessUnitAdminControllerFindBusinessUnitMembersApiResponse =
  /** status 200  */ PaginatedDocumented & {
    data?: BusinessUnitMemberDto[];
  };
export interface BusinessUnitAdminControllerFindBusinessUnitMembersApiArg {
  id: string;
  page?: number;
  pageSize?: number;
  /** Parameter to sort by.
          <p>To sort by multiple fields, provide params as an array. The order in url defines an order of sorting</p>
          <p>
                 <b>Format: </b> sort=[fieldName:direction] where direction is -1 (descending) or 1 (ascending)
              </p>
          <p>
                 <b>Example: </b> name:1
              </p>
          <p>
                 <b>Default Value: </b> sort=[createdAt:-1]
              </p>
           */
  sort?: string[];
  /** Filter by a field in the data..
              <p>
                 <b>Format: </b> filters[fieldName]=VALUE
              </p>
              <p>
                 <b>Example 1: in: </b> filters[fieldName][$in]=VALUE_1&filters[fieldName][$in]=VALUE_2
              </p>
              <p>
                 <b>Example 2: not equal: </b> filters[fieldName][$ne]=VALUE
              </p>
              <h4>Available Operations</h4><ul><li>$eq - equal</li>
    <li>$ne - not equal to</li>
    <li>$gt - greater than</li>
    <li>$gte - greater than or equal to</li>
    <li>$lte - less than or equal to</li>
    <li>$lt - less than</li>
    <li>$in - is in array</li>
    <li>$nin - is not in array</li></ul> */
  filters?: object;
}
export type BusinessUnitAdminControllerUpdatePartnerApiResponse =
  /** status 200  */ PartnerDto;
export interface BusinessUnitAdminControllerUpdatePartnerApiArg {
  id: string;
  updatePartnerDto: UpdatePartnerDto;
}
export type BusinessUnitAdminControllerCreateGroupApiResponse =
  /** status 201  */ PermissionGroupDto;
export interface BusinessUnitAdminControllerCreateGroupApiArg {
  createGroupDto: CreateGroupDto;
}
export type BusinessUnitAdminControllerFindGroupsApiResponse =
  /** status 200  */ PaginatedDocumented & {
    data?: PermissionGroupDto[];
  };
export interface BusinessUnitAdminControllerFindGroupsApiArg {
  page?: number;
  pageSize?: number;
  /** Parameter to sort by.
          <p>To sort by multiple fields, provide params as an array. The order in url defines an order of sorting</p>
          <p>
                 <b>Format: </b> sort=[fieldName:direction] where direction is -1 (descending) or 1 (ascending)
              </p>
          <p>
                 <b>Example: </b> name:1
              </p>
          <p>
                 <b>Default Value: </b> sort=[createdAt:-1]
              </p>
           */
  sort?: string[];
  /** Filter by a field in the data..
              <p>
                 <b>Format: </b> filters[fieldName]=VALUE
              </p>
              <p>
                 <b>Example 1: in: </b> filters[fieldName][$in]=VALUE_1&filters[fieldName][$in]=VALUE_2
              </p>
              <p>
                 <b>Example 2: not equal: </b> filters[fieldName][$ne]=VALUE
              </p>
              <h4>Available Operations</h4><ul><li>$eq - equal</li>
    <li>$ne - not equal to</li>
    <li>$gt - greater than</li>
    <li>$gte - greater than or equal to</li>
    <li>$lte - less than or equal to</li>
    <li>$lt - less than</li>
    <li>$in - is in array</li>
    <li>$nin - is not in array</li></ul> */
  filters?: object;
  businessUnitId: string;
}
export type BusinessUnitAdminControllerUpdateGroupApiResponse =
  /** status 200  */ PermissionGroupDto;
export interface BusinessUnitAdminControllerUpdateGroupApiArg {
  id: string;
  updateGroupDto: UpdateGroupDto;
}
export type BusinessUnitAdminControllerUpdateGroupStatusApiResponse =
  /** status 200  */ PermissionGroupDto;
export interface BusinessUnitAdminControllerUpdateGroupStatusApiArg {
  id: string;
  updateGroupStatusDto: UpdateGroupStatusDto;
}
export type BusinessUnitAdminControllerAddMembershipApiResponse =
  /** status 201  */ MembershipDto;
export interface BusinessUnitAdminControllerAddMembershipApiArg {
  id: string;
  createMembershipDto: CreateMembershipDto;
}
export type BusinessUnitAdminControllerFindMembersApiResponse =
  /** status 200  */ PaginatedDocumented & {
    data?: MembershipDto[];
  };
export interface BusinessUnitAdminControllerFindMembersApiArg {
  id: string;
  page?: number;
  pageSize?: number;
  /** Parameter to sort by.
          <p>To sort by multiple fields, provide params as an array. The order in url defines an order of sorting</p>
          <p>
                 <b>Format: </b> sort=[fieldName:direction] where direction is -1 (descending) or 1 (ascending)
              </p>
          <p>
                 <b>Example: </b> name:1
              </p>
          <p>
                 <b>Default Value: </b> sort=[createdAt:-1]
              </p>
           */
  sort?: string[];
  /** Filter by a field in the data..
              <p>
                 <b>Format: </b> filters[fieldName]=VALUE
              </p>
              <p>
                 <b>Example 1: in: </b> filters[fieldName][$in]=VALUE_1&filters[fieldName][$in]=VALUE_2
              </p>
              <p>
                 <b>Example 2: not equal: </b> filters[fieldName][$ne]=VALUE
              </p>
              <h4>Available Operations</h4><ul><li>$eq - equal</li>
    <li>$ne - not equal to</li>
    <li>$gt - greater than</li>
    <li>$gte - greater than or equal to</li>
    <li>$lte - less than or equal to</li>
    <li>$lt - less than</li>
    <li>$in - is in array</li>
    <li>$nin - is not in array</li></ul> */
  filters?: object;
}
export type BusinessUnitAdminControllerBulkAddMembershipsApiResponse =
  /** status 201  */ MembershipDto[];
export interface BusinessUnitAdminControllerBulkAddMembershipsApiArg {
  id: string;
  bulkCreateMembershipsDto: BulkCreateMembershipsDto;
}
export type BusinessUnitAdminControllerUpdateMembershipApiResponse =
  /** status 200  */ MembershipDto;
export interface BusinessUnitAdminControllerUpdateMembershipApiArg {
  id: string;
  updateMembershipDto: UpdateMembershipDto;
}
export type BusinessUnitAdminControllerRevokeMembershipApiResponse =
  /** status 200  */ object;
export interface BusinessUnitAdminControllerRevokeMembershipApiArg {
  id: string;
}
export type WorkflowExecutionPlanControllerFindManyApiResponse =
  /** status 200  */ PaginatedDocumented & {
    data?: WorkflowExecutionPlanDto[];
  };
export interface WorkflowExecutionPlanControllerFindManyApiArg {
  page?: number;
  pageSize?: number;
  /** Parameter to sort by.
          <p>To sort by multiple fields, provide params as an array. The order in url defines an order of sorting</p>
          <p>
                 <b>Format: </b> sort=[fieldName:direction] where direction is -1 (descending) or 1 (ascending)
              </p>
          <p>
                 <b>Example: </b> name:1
              </p>
          <p>
                 <b>Default Value: </b> sort=[createdAt:-1]
              </p>
           */
  sort?: string[];
  /** Filter by a field in the data..
              <p>
                 <b>Format: </b> filters[fieldName]=VALUE
              </p>
              <p>
                 <b>Example 1: in: </b> filters[fieldName][$in]=VALUE_1&filters[fieldName][$in]=VALUE_2
              </p>
              <p>
                 <b>Example 2: not equal: </b> filters[fieldName][$ne]=VALUE
              </p>
              <h4>Available Operations</h4><ul><li>$eq - equal</li>
    <li>$ne - not equal to</li>
    <li>$gt - greater than</li>
    <li>$gte - greater than or equal to</li>
    <li>$lte - less than or equal to</li>
    <li>$lt - less than</li>
    <li>$in - is in array</li>
    <li>$nin - is not in array</li></ul> */
  filters?: object;
  campaignId?: string;
  /** Filters by operation type for both campaign and site-level listings. */
  operationType?: OperationType;
}
export type WorkflowExecutionPlanControllerFindOneApiResponse =
  /** status 200  */ WorkflowExecutionPlanDto;
export interface WorkflowExecutionPlanControllerFindOneApiArg {
  id: string;
}
export type CampaignTitlesControllerGetCampaignTitlesApiResponse =
  /** status 200  */ CampaignTitle[];
export interface CampaignTitlesControllerGetCampaignTitlesApiArg {
  gtiList?: string[];
  countryCode?: Country;
  locale?: Locale;
  businessLine?: BusinessLine;
  pageId?: string;
  strategyId?: string;
}
export type CampaignTitlesControllerGetCampaignTitlesBatchApiResponse =
  /** status 200  */ CampaignTitle[];
export interface CampaignTitlesControllerGetCampaignTitlesBatchApiArg {
  getCampaignTitlesRequest: GetCampaignTitlesRequest;
}
export type RefTagsControllerCreateApiResponse = /** status 201  */ RefTagDto;
export interface RefTagsControllerCreateApiArg {
  createRefTagDto: CreateRefTagDto;
}
export type RefTagsControllerFindManyApiResponse =
  /** status 200  */ PaginatedDocumented & {
    data?: RefTagDto[];
  };
export interface RefTagsControllerFindManyApiArg {
  page?: number;
  pageSize?: number;
  /** Parameter to sort by.
          <p>To sort by multiple fields, provide params as an array. The order in url defines an order of sorting</p>
          <p>
                 <b>Format: </b> sort=[fieldName:direction] where direction is -1 (descending) or 1 (ascending)
              </p>
          <p>
                 <b>Example: </b> name:1
              </p>
          <p>
                 <b>Default Value: </b> sort=[createdAt:-1]
              </p>
           */
  sort?: string[];
  /** Filter by a field in the data..
              <p>
                 <b>Format: </b> filters[fieldName]=VALUE
              </p>
              <p>
                 <b>Example 1: in: </b> filters[fieldName][$in]=VALUE_1&filters[fieldName][$in]=VALUE_2
              </p>
              <p>
                 <b>Example 2: not equal: </b> filters[fieldName][$ne]=VALUE
              </p>
              <h4>Available Operations</h4><ul><li>$eq - equal</li>
    <li>$ne - not equal to</li>
    <li>$gt - greater than</li>
    <li>$gte - greater than or equal to</li>
    <li>$lte - less than or equal to</li>
    <li>$lt - less than</li>
    <li>$in - is in array</li>
    <li>$nin - is not in array</li></ul> */
  filters?: object;
  programId?: string;
  assetId?: string;
  campaignId?: string;
}
export type RefTagsControllerCreateBulkApiResponse =
  /** status 201  */ RefTagDto[];
export interface RefTagsControllerCreateBulkApiArg {
  createRefTagsDto: CreateRefTagsDto;
}
export type RefTagsControllerGetApiResponse = /** status 200  */ RefTagDto;
export interface RefTagsControllerGetApiArg {
  id: string;
}
export type RefTagsControllerEmitAndesEventApiResponse =
  /** status 201  */ RefTagDto;
export interface RefTagsControllerEmitAndesEventApiArg {
  id: string;
}
export type WorkflowsControllerCreateApiResponse =
  /** status 201  */ WorkflowDto;
export interface WorkflowsControllerCreateApiArg {
  createWorkflowDto: CreateWorkflowDto;
}
export type WorkflowsControllerFindManyApiResponse =
  /** status 200  */ PaginatedDocumented & {
    data?: WorkflowDto[];
  };
export interface WorkflowsControllerFindManyApiArg {
  page?: number;
  pageSize?: number;
  /** Parameter to sort by.
          <p>To sort by multiple fields, provide params as an array. The order in url defines an order of sorting</p>
          <p>
                 <b>Format: </b> sort=[fieldName:direction] where direction is -1 (descending) or 1 (ascending)
              </p>
          <p>
                 <b>Example: </b> name:1
              </p>
          <p>
                 <b>Default Value: </b> sort=[createdAt:-1]
              </p>
           */
  sort?: string[];
  /** Filter by a field in the data..
              <p>
                 <b>Format: </b> filters[fieldName]=VALUE
              </p>
              <p>
                 <b>Example 1: in: </b> filters[fieldName][$in]=VALUE_1&filters[fieldName][$in]=VALUE_2
              </p>
              <p>
                 <b>Example 2: not equal: </b> filters[fieldName][$ne]=VALUE
              </p>
              <h4>Available Operations</h4><ul><li>$eq - equal</li>
    <li>$ne - not equal to</li>
    <li>$gt - greater than</li>
    <li>$gte - greater than or equal to</li>
    <li>$lte - less than or equal to</li>
    <li>$lt - less than</li>
    <li>$in - is in array</li>
    <li>$nin - is not in array</li></ul> */
  filters?: object;
  programId: string;
  campaignId?: string;
}
export type WorkflowsControllerFindQueuedProgramsWithCampaignsApiResponse =
  unknown;
export type WorkflowsControllerFindQueuedProgramsWithCampaignsApiArg = void;
export type WorkflowsControllerFindNextQueuedWorkflowsApiResponse =
  /** status 200  */ WorkflowDto[];
export interface WorkflowsControllerFindNextQueuedWorkflowsApiArg {
  limit: number;
  inProgressSince: string;
}
export type WorkflowsControllerFindOneApiResponse =
  /** status 200  */ WorkflowDto;
export interface WorkflowsControllerFindOneApiArg {
  id: string;
}
export type WorkflowsControllerUpdateApiResponse =
  /** status 200  */ WorkflowDto;
export interface WorkflowsControllerUpdateApiArg {
  id: string;
  updateWorkflowDto: UpdateWorkflowDto;
}
export type WorkflowsControllerDeleteApiResponse =
  /** status 200  */ WorkflowDto;
export interface WorkflowsControllerDeleteApiArg {
  id: string;
}
export type DistinctValuesControllerGetCampaignOptionsApiResponse = unknown;
export type DistinctValuesControllerGetCampaignOptionsApiArg = void;
export type DistinctValuesControllerGetDistinctTitlesApiResponse =
  /** status 200 Paginated list of distinct titles with metadata */ PaginatedTitlesResponse;
export interface DistinctValuesControllerGetDistinctTitlesApiArg {
  page?: number;
  pageSize?: number;
  country?: string;
  locale?: string;
  search?: string;
  sort?: string[];
  campaignIds?: string[];
  gti?: string;
  hasActiveCampaigns?: boolean;
  publisher?: string;
}
export type DistinctValuesControllerGetDistinctValuesApiResponse =
  /** status 200  */ object[];
export interface DistinctValuesControllerGetDistinctValuesApiArg {
  entityType: 'asset' | 'campaign';
  field: string;
  programId?: string;
  /** Filter by a field in the data. Format: filters[fieldName]=VALUE for single values or filters[fieldName]=value1,value2 for arrays. Example 1: filters[campaignIds]=campaign1,campaign2. Example 2: filters[approvalStatus]=PENDING. */
  filters?: object;
}
export type GroupTitlesControllerGroupTitlesApiResponse =
  /** status 201  */ GroupTitlesResponse;
export interface GroupTitlesControllerGroupTitlesApiArg {
  groupTitlesRequest: GroupTitlesRequest;
}
export type QuicksightControllerGenerateTiktokVtaDashboardEmbedUrlApiResponse =
  /** status 201  */ GeneratePolarisEmbedUrlResponseDto;
export type QuicksightControllerGenerateTiktokVtaDashboardEmbedUrlApiArg = void;
export type CampaignPlansControllerCreateApiResponse =
  /** status 201 Campaign plan created */ CampaignPlanDto;
export interface CampaignPlansControllerCreateApiArg {
  createCampaignPlanDto: CreateCampaignPlanDto;
}
export type CampaignPlansControllerFindByCampaignIdApiResponse =
  /** status 200  */ CampaignPlanDto;
export interface CampaignPlansControllerFindByCampaignIdApiArg {
  campaignId: string;
}
export type CampaignPlansControllerFindAllByCampaignIdApiResponse =
  /** status 200  */ CampaignPlanDto[];
export interface CampaignPlansControllerFindAllByCampaignIdApiArg {
  campaignId: string;
  status?: 'IN_PROGRESS' | 'APPROVED' | 'CANCELLED';
}
export type CampaignPlansControllerFindOneApiResponse =
  /** status 200  */ CampaignPlanDto;
export interface CampaignPlansControllerFindOneApiArg {
  id: string;
}
export type CampaignPlansControllerUpdateApiResponse =
  /** status 200  */ CampaignPlanDto;
export interface CampaignPlansControllerUpdateApiArg {
  id: string;
  updateCampaignPlanDto: UpdateCampaignPlanDto;
}
export type CampaignPlansControllerUpdateStatusApiResponse = /** status 200  */
  | CampaignPlanDto
  | /** status 201  */ CampaignPlanDto;
export interface CampaignPlansControllerUpdateStatusApiArg {
  id: string;
  updateStatusDto: UpdateStatusDto;
}
export type SonataApiControllerSearchPageIdsApiResponse =
  /** status 200  */ string[];
export interface SonataApiControllerSearchPageIdsApiArg {
  pageType: string;
}
export type CatalogControllerGetCatalogTitlesApiResponse =
  /** status 200  */ GetCatalogGtiResponse[];
export interface CatalogControllerGetCatalogTitlesApiArg {
  getCatalogGtiRequest: GetCatalogGtiRequest;
}
export type SocialSignalsControllerGetRecommendationsApiResponse =
  /** status 200  */ PaginatedDocumented & {
    data?: SocialSignalsRecommendation[];
  };
export interface SocialSignalsControllerGetRecommendationsApiArg {
  country?: string;
  locale?: string;
  riskLabel?: string;
  sentiment?: string;
  sourcePlatform?: string;
  trendSearch?: string;
  startDate?: string;
  endDate?: string;
  topic?: string;
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  pageSize?: number;
}
export type PaidMediaDashboardControllerGetInsightsApiResponse =
  /** status 200  */ object;
export interface PaidMediaDashboardControllerGetInsightsApiArg {
  paidMediaInsightsQueryDto: PaidMediaInsightsQueryDto;
}
export type PaidMediaDashboardControllerQueryApiResponse =
  /** status 200  */ object;
export interface PaidMediaDashboardControllerQueryApiArg {
  paidMediaDashboardQueryDto: PaidMediaDashboardQueryDto;
}
export type PaidMediaDashboardControllerStartExportApiResponse = unknown;
export interface PaidMediaDashboardControllerStartExportApiArg {
  paidMediaDashboardExportDto: PaidMediaDashboardExportDto;
}
export type PaidMediaDashboardControllerGetExportStatusApiResponse =
  /** status 200  */ object;
export interface PaidMediaDashboardControllerGetExportStatusApiArg {
  exportId: string;
}
export type AgentSessionsControllerCreateApiResponse =
  /** status 201  */ AgentSessionDto;
export interface AgentSessionsControllerCreateApiArg {
  createAgentSessionDto: CreateAgentSessionDto;
}
export type AgentSessionsControllerFindManyApiResponse = unknown;
export interface AgentSessionsControllerFindManyApiArg {
  /** Filter by entity ID */
  entityId?: string;
  /** Filter by entity type */
  entityType?: 'campaign';
  /** Filter by user ID */
  userId?: string;
  page?: number;
  pageSize?: number;
  sort?: string[];
}
export type AgentSessionsControllerFindOneApiResponse =
  /** status 200  */ AgentSessionDto;
export interface AgentSessionsControllerFindOneApiArg {
  sessionId: string;
}
export type AgentSessionsControllerUpdateApiResponse =
  /** status 200  */ AgentSessionDto;
export interface AgentSessionsControllerUpdateApiArg {
  sessionId: string;
  updateAgentSessionDto: UpdateAgentSessionDto;
}
export type AgentSessionsControllerRemoveApiResponse =
  /** status 200  */ object;
export interface AgentSessionsControllerRemoveApiArg {
  sessionId: string;
}
export type PublisherEntitiesControllerListForCampaignApiResponse =
  /** status 200  */ PaginatedDocumented & {
    data?: PublisherEntityDto[];
  };
export interface PublisherEntitiesControllerListForCampaignApiArg {
  campaignId: string;
  /** Number of publisher entities per page (maximum 100) */
  pageSize?: number;
  /** Sort as "field:1" (asc) or "field:-1" (desc); default createdAt:-1 */
  sort?: string[];
  publisher: Publisher;
  publisherEntityType?: PublisherEntityType;
  entityStatus?: EntityStatus;
  syncStatus?: SyncStatus;
  /** Filter by the parent source entity identifier */
  parentSourceEntityId?: string;
  /** Filter by any of these parent source entity identifiers (maximum 100). Send repeated/indexed params; a single comma-separated value is also accepted for ids without commas. Mutually exclusive with parentSourceEntityId */
  parentSourceEntityIds?: string[];
  /** Filter to children of these parent publisher-entity rows, by row _id (maximum 100). Parents are resolved within the same campaign and publisher. Send repeated/indexed params or one comma-separated value. Mutually exclusive with parentSourceEntityId and parentSourceEntityIds */
  parentEntityIds?: string[];
  /** Filter by the parent publisher entity identifier (publisher-side parent ID) */
  parentPublisherEntityId?: string;
  page?: number;
}
export type AssetRuleEvaluationControllerEvictByRuleIdApiResponse =
  /** status 200  */ EvictionResultDto;
export interface AssetRuleEvaluationControllerEvictByRuleIdApiArg {
  ruleId: string;
}
export type AssetRuleEvaluationControllerTriggerAdhocApiResponse =
  /** status 202  */ AdhocExecutionResponseDto;
export interface AssetRuleEvaluationControllerTriggerAdhocApiArg {
  adhocEvaluationDto: AdhocEvaluationDto;
}
export type AssetRuleEvaluationControllerFindByAssetIdApiResponse =
  /** status 200  */ AssetRuleEvaluationDto[];
export interface AssetRuleEvaluationControllerFindByAssetIdApiArg {
  assetId: string;
  /** Narrow to a single rule */
  ruleId?: string;
}
export type AssetRuleEvaluationControllerEvictByAssetIdApiResponse =
  /** status 200  */ EvictionResultDto;
export interface AssetRuleEvaluationControllerEvictByAssetIdApiArg {
  assetId: string;
}
export type BrandControllerGetAvailablePresetsApiResponse =
  /** status 200 Available brand presets with logo and name */ BrandPresetsResponseDto;
export type BrandControllerGetAvailablePresetsApiArg = void;
export interface EntityMetricsControllerGetFieldsApiResponse {
  entity?: string;
  fields?: string[];
}
export interface EntityMetricsControllerGetFieldsApiArg {
  /** Entity type */
  entity: 'campaigns' | 'assets' | 'workflows';
}
export type EntityMetricsControllerQueryApiResponse =
  /** status 200 Aggregated metrics data */ EntityMetricsResponseDto;
export interface EntityMetricsControllerQueryApiArg {
  entityMetricsQueryDto: EntityMetricsQueryDto;
}
export type DocsControllerListPagesApiResponse =
  /** status 200  */ DocsListResponseDto;
export interface DocsControllerListPagesApiArg {
  app: string;
}
export type DocsControllerSearchApiResponse =
  /** status 200  */ DocsSearchHitDto[];
export interface DocsControllerSearchApiArg {
  app: string;
  /** Search text; trimmed, then 2 to 100 characters. */
  q: string;
}
export type DocsControllerGetPageApiResponse =
  /** status 200  */ DocPageResponseDto;
export interface DocsControllerGetPageApiArg {
  app: string;
  folder: string;
  page: string;
}
export type IssueReportsControllerCreateApiResponse =
  /** status 201 Ticket created */ IssueReportResponseDto;
export interface IssueReportsControllerCreateApiArg {
  createIssueReportDto: CreateIssueReportDto;
}
export interface UserContextDto {
  username: string;
  userType: 'internal' | 'external' | 'service';
  email?: string;
  given_name?: string;
  family_name?: string;
  isAdmin?: boolean;
  /** Present and true only for the canary automation principal, whose admin grant bypasses every permission check. */
  isAutomationBypass?: boolean;
  featureAccess?: Record<string, boolean>;
}
export interface AuditEventId {
  publisher?: string;
  id: string;
  type:
  | 'ASSET'
  | 'PROGRAM'
  | 'CAMPAIGN'
  | 'BLOCKED_GTI'
  | 'BUSINESS_UNIT'
  | 'PARTNER'
  | 'PERMISSION_GROUP'
  | 'USER_MEMBERSHIP';
  availabilityAction?: 'MARK_IN_STOCK' | 'MARK_OUT_OF_STOCK';
}
export interface Program {}
export interface AuditEvent {
  user: string;
  operation:
  | 'PROGRAM_CREATED'
  | 'PROGRAM_UPDATED'
  | 'PROGRAM_DELETED'
  | 'CAMPAIGN_DESIRED_CREATED'
  | 'CAMPAIGN_DESIRED_UPDATED'
  | 'CAMPAIGN_DESIRED_DELETED'
  | 'ASSET_CREATED'
  | 'ASSET_UPDATED'
  | 'ASSET_DELETED'
  | 'ASSET_SCHEDULE_TRANSITION'
  | 'GLOBAL_BLOCKLIST_ENTRY_ADDED'
  | 'GLOBAL_BLOCKLIST_ENTRY_DELETED'
  | 'ROLE_GRANTED'
  | 'ROLE_REVOKED'
  | 'ROLE_UPDATED'
  | 'BU_CREATED'
  | 'BU_UPDATED'
  | 'BU_DELETED'
  | 'PARTNER_CREATED'
  | 'PARTNER_UPDATED'
  | 'GROUP_CREATED'
  | 'GROUP_UPDATED'
  | 'GROUP_DEACTIVATED'
  | 'GROUP_ARCHIVED'
  | 'CAMPAIGN_PLAN_CREATED'
  | 'CAMPAIGN_PLAN_UPDATED'
  | 'CAMPAIGN_PLAN_APPROVED'
  | 'CAMPAIGN_PLAN_CANCELLED';
  ids: AuditEventId[];
  collection: string;
  programState?: Program;
  timestamp: string;
  level: 'INFO' | 'WARN' | 'ERROR';
  message?: string;
}
export interface Money {
  amount: number;
  currency:
  | 'ADP'
  | 'AED'
  | 'AFA'
  | 'AFN'
  | 'ALK'
  | 'ALL'
  | 'AMD'
  | 'ANG'
  | 'AOA'
  | 'AOK'
  | 'AON'
  | 'AOR'
  | 'ARA'
  | 'ARP'
  | 'ARS'
  | 'ARY'
  | 'ATS'
  | 'AUD'
  | 'AWG'
  | 'AYM'
  | 'AZM'
  | 'AZN'
  | 'BAD'
  | 'BAM'
  | 'BBD'
  | 'BDT'
  | 'BEC'
  | 'BEF'
  | 'BEL'
  | 'BGJ'
  | 'BGK'
  | 'BGL'
  | 'BGN'
  | 'BHD'
  | 'BIF'
  | 'BMD'
  | 'BND'
  | 'BOB'
  | 'BOP'
  | 'BOV'
  | 'BRB'
  | 'BRC'
  | 'BRE'
  | 'BRL'
  | 'BRN'
  | 'BRR'
  | 'BSD'
  | 'BTN'
  | 'BUK'
  | 'BWP'
  | 'BYB'
  | 'BYN'
  | 'BYR'
  | 'BZD'
  | 'CAD'
  | 'CDF'
  | 'CHC'
  | 'CHE'
  | 'CHF'
  | 'CHW'
  | 'CLF'
  | 'CLP'
  | 'CNY'
  | 'COP'
  | 'COU'
  | 'CRC'
  | 'CSD'
  | 'CSJ'
  | 'CSK'
  | 'CUC'
  | 'CUP'
  | 'CVE'
  | 'CYP'
  | 'CZK'
  | 'DDM'
  | 'DEM'
  | 'DJF'
  | 'DKK'
  | 'DOP'
  | 'DZD'
  | 'ECS'
  | 'ECV'
  | 'EEK'
  | 'EGP'
  | 'ERN'
  | 'ESA'
  | 'ESB'
  | 'ESP'
  | 'ETB'
  | 'EUR'
  | 'FIM'
  | 'FJD'
  | 'FKP'
  | 'FRF'
  | 'GBP'
  | 'GEK'
  | 'GEL'
  | 'GHC'
  | 'GHP'
  | 'GHS'
  | 'GIP'
  | 'GMD'
  | 'GNE'
  | 'GNF'
  | 'GNS'
  | 'GQE'
  | 'GRD'
  | 'GTQ'
  | 'GWE'
  | 'GWP'
  | 'GYD'
  | 'HKD'
  | 'HNL'
  | 'HRD'
  | 'HRK'
  | 'HTG'
  | 'HUF'
  | 'IDR'
  | 'IEP'
  | 'ILP'
  | 'ILR'
  | 'ILS'
  | 'INR'
  | 'IQD'
  | 'IRR'
  | 'ISJ'
  | 'ISK'
  | 'ITL'
  | 'JMD'
  | 'JOD'
  | 'JPY'
  | 'KES'
  | 'KGS'
  | 'KHR'
  | 'KMF'
  | 'KPW'
  | 'KRW'
  | 'KWD'
  | 'KYD'
  | 'KZT'
  | 'LAJ'
  | 'LAK'
  | 'LBP'
  | 'LKR'
  | 'LRD'
  | 'LSL'
  | 'LSM'
  | 'LTL'
  | 'LTT'
  | 'LUC'
  | 'LUF'
  | 'LUL'
  | 'LVL'
  | 'LVR'
  | 'LYD'
  | 'MAD'
  | 'MDL'
  | 'MGA'
  | 'MGF'
  | 'MKD'
  | 'MLF'
  | 'MMK'
  | 'MNT'
  | 'MOP'
  | 'MRO'
  | 'MRU'
  | 'MTL'
  | 'MTP'
  | 'MUR'
  | 'MVQ'
  | 'MVR'
  | 'MWK'
  | 'MXN'
  | 'MXP'
  | 'MXV'
  | 'MYR'
  | 'MZE'
  | 'MZM'
  | 'MZN'
  | 'NAD'
  | 'NGN'
  | 'NIC'
  | 'NIO'
  | 'NLG'
  | 'NOK'
  | 'NPR'
  | 'NZD'
  | 'OMR'
  | 'PAB'
  | 'PEH'
  | 'PEI'
  | 'PEN'
  | 'PES'
  | 'PGK'
  | 'PHP'
  | 'PKR'
  | 'PLN'
  | 'PLZ'
  | 'PTE'
  | 'PYG'
  | 'QAR'
  | 'RHD'
  | 'ROK'
  | 'ROL'
  | 'RON'
  | 'RSD'
  | 'RUB'
  | 'RUR'
  | 'RWF'
  | 'SAR'
  | 'SBD'
  | 'SCR'
  | 'SDD'
  | 'SDG'
  | 'SDP'
  | 'SEK'
  | 'SGD'
  | 'SHP'
  | 'SIT'
  | 'SKK'
  | 'SLL'
  | 'SOS'
  | 'SRD'
  | 'SRG'
  | 'SSP'
  | 'STD'
  | 'STN'
  | 'SUR'
  | 'SVC'
  | 'SYP'
  | 'SZL'
  | 'THB'
  | 'TJR'
  | 'TJS'
  | 'TMM'
  | 'TMT'
  | 'TND'
  | 'TOP'
  | 'TPE'
  | 'TRL'
  | 'TRY'
  | 'TTD'
  | 'TWD'
  | 'TZS'
  | 'UAH'
  | 'UAK'
  | 'UGS'
  | 'UGW'
  | 'UGX'
  | 'USD'
  | 'USN'
  | 'USS'
  | 'UYI'
  | 'UYN'
  | 'UYP'
  | 'UYU'
  | 'UYW'
  | 'UZS'
  | 'VEB'
  | 'VEF'
  | 'VES'
  | 'VNC'
  | 'VND'
  | 'VUV'
  | 'WST'
  | 'XAF'
  | 'XAG'
  | 'XAU'
  | 'XBA'
  | 'XBB'
  | 'XBC'
  | 'XBD'
  | 'XCD'
  | 'XDR'
  | 'XEU'
  | 'XFO'
  | 'XFU'
  | 'XOF'
  | 'XPD'
  | 'XPF'
  | 'XPT'
  | 'XRE'
  | 'XSU'
  | 'XTS'
  | 'XUA'
  | 'XXX'
  | 'YDD'
  | 'YER'
  | 'YUD'
  | 'YUM'
  | 'YUN'
  | 'ZAR'
  | 'ZMK'
  | 'ZMW'
  | 'ZRN'
  | 'ZRZ'
  | 'ZWC'
  | 'ZWD'
  | 'ZWL'
  | 'ZWN'
  | 'ZWR';
}
export interface Schedule {
  startTime?: string;
  endTime?: string;
  refreshScheduleCron: string;
}
export interface ProgramDto {
  name: string;
  countries: object;
  globalBudget?: Money;
  schedule: Schedule;
  status: 'ACTIVE' | 'DELETED' | 'PAUSED';
  gtiBlocklist: string[];
  owner?: string;
  userSharedList?: string[];
  createdAt?: object;
  updatedAt?: object;
  programId: string;
  campaigns: string[];
}
export interface CreateProgramDto {
  name: string;
  countries: object;
  globalBudget?: Money;
  schedule: Schedule;
  status: 'ACTIVE' | 'DELETED' | 'PAUSED';
  gtiBlocklist: string[];
  owner?: string;
  userSharedList?: string[];
}
export interface PaginatedMetaPaginationDocumented {
  pageSize: number;
  total: number;
  page: number;
  pageCount: number;
}
export interface PaginatedMetaDocumented {
  pagination: PaginatedMetaPaginationDocumented;
}
export interface PaginatedDocumented {
  data: object[];
  meta: PaginatedMetaDocumented;
}
export interface UpdateProgramDto {
  countries?: object;
  globalBudget?: Money;
  schedule?: Schedule;
  status?: 'ACTIVE' | 'DELETED' | 'PAUSED';
  gtiBlocklist?: string[];
  owner?: string;
  userSharedList?: string[];
}
export type Locale =
  | 'ar_AE'
  | 'bn_IN'
  | 'cs_CZ'
  | 'da_DK'
  | 'de_CH'
  | 'de_DE'
  | 'en_AE'
  | 'en_AU'
  | 'en_CA'
  | 'en_GB'
  | 'en_IE'
  | 'en_IN'
  | 'en_NG'
  | 'en_NZ'
  | 'en_SG'
  | 'en_US'
  | 'en_ZA'
  | 'es_419'
  | 'es_AR'
  | 'es_BO'
  | 'es_CL'
  | 'es_CO'
  | 'es_CR'
  | 'es_DO'
  | 'es_EC'
  | 'es_ES'
  | 'es_GT'
  | 'es_HN'
  | 'es_MX'
  | 'es_NI'
  | 'es_PA'
  | 'es_PE'
  | 'es_PR'
  | 'es_PY'
  | 'es_SV'
  | 'es_US'
  | 'es_UY'
  | 'es_VE'
  | 'fi_FI'
  | 'fr_BE'
  | 'fr_CA'
  | 'fr_CH'
  | 'fr_FR'
  | 'he_IL'
  | 'hu_HU'
  | 'hi_IN'
  | 'id_ID'
  | 'it_CH'
  | 'it_IT'
  | 'ja_JP'
  | 'kn_IN'
  | 'ko_KR'
  | 'ml_IN'
  | 'mr_IN'
  | 'ms_MY'
  | 'nl_BE'
  | 'nl_NL'
  | 'no_NO'
  | 'pl_PL'
  | 'pt_BR'
  | 'pt_PT'
  | 'ro_RO'
  | 'sv_SE'
  | 'ta_IN'
  | 'te_IN'
  | 'th_TH'
  | 'tr_TR'
  | 'zh_CN'
  | 'zh_TW';
export type RejectReason =
  | 'SUBJECT_CUT_OFF'
  | 'AWKWARD_FRAMING'
  | 'BLURRY_LOW_RESOLUTION'
  | 'KEY_ELEMENT_MISSING'
  | 'WRONG_ARTWORK'
  | 'SENSITIVE_OFF_BRAND'
  | 'INCORRECT_MESSAGING'
  | 'DO_NOT_PROMOTE_TITLE'
  | 'OTHER';
export interface DestinationUrl {
  campaignId: string;
  templateConfigId?: string;
  url: string;
}
export interface Title {
  name?: string;
  gti: string;
}
export interface AssetMetadataDto {
  width?: number;
  height?: number;
  videoDuration?: number;
  channel?: string[];
  videoBitrate?: number;
  audioBitrate?: number;
  liveStatus?: string;
  originalFilename?: string;
  generationMethods?: string[];
}
export interface ThumbnailAssetDto {
  locale?: Locale;
  approvalStatus: 'APPROVED' | 'PENDING' | 'REJECTED' | 'BLOCKED';
  assetId: string;
  assetUrl: string;
  source: 'BENDER';
  sourceId: string;
  metadata?: AssetMetadataDto;
  legacyAssetId?: string;
}
export interface AssetScheduleDto {
  startTime: string;
  endTime: string;
  ignoreScheduleChanges?: boolean;
}
export interface EvaluatedRuleDto {
  ruleId: string;
  title?: string;
  severity?: 'BLOCKER' | 'WARNING' | 'INFO';
  status: 'PASSED' | 'VIOLATED' | 'FAILED';
  reason?: string;
}
export interface AssetDto {
  locale?: Locale;
  rejectReason?: RejectReason;
  rejectReasonNote?: string;
  destinationUrl?: string;
  destinationUrls: DestinationUrl[];
  destinationUrlType?: 'DETAILS_PAGE' | 'LANDING_PAGE';
  titles: Title[];
  assetType: 'IMAGE' | 'VIDEO';
  campaignIds: string[];
  campaignId?: string;
  programId?: string;
  templateConfigIds?: string[];
  productStrategyId?: string;
  thumbnailOptions?: ThumbnailAssetDto[];
  metadata?: AssetMetadataDto;
  schedule?: AssetScheduleDto;
  evaluatedRules?: EvaluatedRuleDto[];
  evaluatedAt?: string;
  createdAt?: object;
  updatedAt?: object;
  approvalStatus: 'APPROVED' | 'PENDING' | 'REJECTED' | 'BLOCKED';
  assetId: string;
  assetUrl: string;
  source: 'BENDER';
  sourceId: string;
  legacyAssetId?: string;
}
export interface CreateAssetDto {
  locale?: Locale;
  approvalStatus: 'APPROVED' | 'PENDING' | 'REJECTED' | 'BLOCKED';
  assetId: string;
  assetUrl: string;
  source: 'BENDER';
  sourceId: string;
  metadata?: AssetMetadataDto;
  legacyAssetId?: string;
  destinationUrl?: string;
  destinationUrlType?: 'DETAILS_PAGE' | 'LANDING_PAGE';
  titles: Title[];
  assetType: 'IMAGE' | 'VIDEO';
  campaignIds: string[];
  campaignId?: string;
  programId?: string;
  templateConfigIds?: string[];
  productStrategyId?: string;
  thumbnailOptions?: ThumbnailAssetDto[];
  schedule?: AssetScheduleDto;
}
export interface CreateAssetsDto {
  programId: string;
  campaignId?: string;
  assets: CreateAssetDto[];
}
export interface ObjectId {}
export interface UpdateAssetDto {
  rejectReason?: RejectReason;
  assetId: string;
  rejectReasonNote?: string;
  programId: ObjectId;
  campaignIds?: string[];
  thumbnailOptions?: ThumbnailAssetDto[];
  metadata?: AssetMetadataDto;
  templateConfigIds?: string[];
  productStrategyId?: string;
  schedule?: AssetScheduleDto;
  approvalStatus: 'APPROVED' | 'PENDING' | 'REJECTED' | 'BLOCKED';
}
export interface UpdateAssetsDto {
  campaignId?: string;
  assets: UpdateAssetDto[];
}
export interface DeleteAssetsDto {
  assetId: string[];
  productStrategyId?: string;
}
export type PublishStatusState =
  | 'NEVER_PUBLISHED'
  | 'PENDING'
  | 'SYNCED'
  | 'FAILED'
  | 'PARTIAL';
export interface PublishStatusSummaryDto {
  state: PublishStatusState;
  failedCount: number;
  pendingCount: number;
  lastSyncAt?: string;
}
export type CampaignPlanStatus = 'IN_PROGRESS' | 'APPROVED' | 'CANCELLED';
export interface GooglePublisherAccountConfig {
  /** Account timezone (IANA name, e.g. America/Los_Angeles) */
  timezone: string;
  /** Google Ads MCC (Manager) account ID */
  mccAccountId: string;
  /** Google Ads customer (advertiser) account ID */
  customerAccountId: string;
  /** Brand type for this account. Drives logo and business name for all campaigns. */
  brandType?: 'PRIME' | 'AMAZON_PRIME' | 'PRIME_VIDEO';
  /** Account budget currency (Google-supported). Chosen at account onboarding; budgets and bids are entered and displayed in this currency. Legacy accounts without a stored currency resolve to USD. */
  currency:
  | 'USD'
  | 'EUR'
  | 'GBP'
  | 'CAD'
  | 'AUD'
  | 'CHF'
  | 'JPY'
  | 'INR'
  | 'BRL'
  | 'MXN'
  | 'AED'
  | 'SAR'
  | 'ZAR'
  | 'SGD'
  | 'MYR'
  | 'IDR'
  | 'PHP'
  | 'THB'
  | 'TRY'
  | 'PLN'
  | 'CZK'
  | 'HUF'
  | 'RON'
  | 'NOK'
  | 'SEK'
  | 'DKK'
  | 'NZD'
  | 'NGN'
  | 'EGP'
  | 'ARS'
  | 'CLP'
  | 'COP'
  | 'PEN'
  | 'BOB';
  /** Display symbol for `currency` (e.g. '$', '€', 'R$'), for UI budget/bid inputs and agent prompts. Server-resolved, response-only. */
  currencySymbol?: string;
  /** Minimum DAILY budget in `currency` (major units) — the Google DemandGen per-day floor. Clients (agent + UI) MUST enforce this as a hard minimum and reject a lower daily budget before submitting; Google is the final authority and re-validates the exact per-currency minimum at CAMPAIGN_SETUP (BUDGET_BELOW_PER_DAY_MINIMUM). Always resolved (account override, else the currency default). Server-resolved, response-only. */
  minDailyBudget?: number;
}
export interface MetaCatalogConfig {
  /** Account timezone (IANA name, e.g. America/Los_Angeles) */
  timezone: string;
  /** Meta catalog ID. Absent when the publisher account is account-level (adAccountId only, no catalog). */
  catalogId?: string;
}
export interface TikTokCatalogConfig {
  /** Account timezone (IANA name, e.g. America/Los_Angeles) */
  timezone: string;
  /** TikTok catalog ID. Absent when the publisher account is account-level (advertiserId only, no catalog). */
  catalogId?: string;
  /** TikTok business center ID. Absent when the publisher account has none, as with an account-level (advertiserId only) account. */
  businessCenterId?: string;
}
export interface AdspCatalogConfig {
  /** Account timezone (IANA name, e.g. America/Los_Angeles) */
  timezone: string;
  /** ADSP advertiser ID */
  advertiserId: string;
  /** ADSP user ID */
  userId: string;
}
export interface PublisherAccountConfigDto {
  google?: GooglePublisherAccountConfig;
  meta?: MetaCatalogConfig;
  tikTok?: TikTokCatalogConfig;
  adsp?: AdspCatalogConfig;
}
export interface BudgetStrategy {
  dailyBudget: Money;
  lifetimeBudget: Money;
  billingEvent:
  | 'APP_INSTALLS'
  | 'CLICKS'
  | 'IMPRESSIONS'
  | 'LINK_CLICKS'
  | 'NONE'
  | 'OFFER_CLAIMS'
  | 'PAGE_LIKES'
  | 'POST_ENGAGEMENT'
  | 'THRUPLAY'
  | 'PURCHASE'
  | 'LISTING_INTERACTION';
}
export interface Placement {
  placementType:
  | 'FACEBOOK_FEED'
  | 'FACEBOOK_VIDEO_FEEDS'
  | 'FACEBOOK_STORY'
  | 'FACEBOOK_SEARCH'
  | 'FACEBOOK_INSTREAM_VIDEO'
  | 'FACEBOOK_REELS'
  | 'FACEBOOK_PROFILE_FEED'
  | 'INSTAGRAM_STREAM'
  | 'INSTAGRAM_STORY'
  | 'INSTAGRAM_EXPLORE'
  | 'INSTAGRAM_EXPLORE_HOME'
  | 'INSTAGRAM_REELS'
  | 'INSTAGRAM_PROFILE_FEED'
  | 'INSTAGRAM_SEARCH'
  | 'TIKTOK'
  | 'TIKTOK_GLOBAL_APP_BUNDLE'
  | 'TIKTOK_PANGLE'
  | 'GOOGLE_YOUTUBE_IN_FEED'
  | 'GOOGLE_YOUTUBE_IN_STREAM'
  | 'GOOGLE_YOUTUBE_SHORTS'
  | 'GOOGLE_DISCOVER'
  | 'GOOGLE_GMAIL'
  | 'ADSP_DISPLAY_DESKTOP_WEB'
  | 'ADSP_DISPLAY_MOBILE_APP'
  | 'ADSP_ONLINE_VIDEO'
  | 'ADSP_STREAMING_TV';
  benderTemplateId: string;
  strategyId: string;
}
export type Country =
  | 'AE'
  | 'AR'
  | 'AU'
  | 'BE'
  | 'BO'
  | 'BR'
  | 'CA'
  | 'CH'
  | 'CL'
  | 'CO'
  | 'CR'
  | 'CZ'
  | 'DE'
  | 'DK'
  | 'DO'
  | 'EC'
  | 'EG'
  | 'ES'
  | 'FI'
  | 'FR'
  | 'GB'
  | 'GT'
  | 'HN'
  | 'HU'
  | 'ID'
  | 'IE'
  | 'IN'
  | 'IT'
  | 'JP'
  | 'MX'
  | 'MY'
  | 'NG'
  | 'NI'
  | 'NL'
  | 'NO'
  | 'NZ'
  | 'PA'
  | 'PE'
  | 'PH'
  | 'PL'
  | 'PR'
  | 'PT'
  | 'PY'
  | 'RO'
  | 'SA'
  | 'SE'
  | 'SG'
  | 'SV'
  | 'TH'
  | 'TR'
  | 'US'
  | 'UY'
  | 'VE'
  | 'ZA';
export type BusinessLine =
  | 'SVOD'
  | 'TVOD'
  | 'CHANNEL'
  | 'Sports SVOD'
  | 'Sports TVOD'
  | 'Sports Channel';
export type AssetType = 'IMAGE' | 'VIDEO';
export interface DetailPageLinkConfig {
  type: 'DETAIL' | 'HOME' | 'LANDING';
}
export interface HomePageLinkConfig {
  type: 'DETAIL' | 'HOME' | 'LANDING';
}
export interface LandingPageLinkConfig {
  type: 'DETAIL' | 'HOME' | 'LANDING';
  /** Page type for landing page */
  pageType: string;
  /** Page ID for landing page */
  pageId: string;
}
export interface FixedScheduleFieldWindow {
  mode: 'FIXED';
  daysBeforeEvent: number;
  timeOfDay: string;
}
export interface EventScheduleFieldWindow {
  mode: 'EVENT';
  offset: number;
  unit: 'HOURS' | 'DAYS';
  direction: 'BEFORE' | 'AFTER';
  anchor: 'EVENT_START' | 'EVENT_END';
}
export interface TemplateScheduleWindow {
  goesLive:
  | ({
    mode: 'FIXED';
  } & FixedScheduleFieldWindow)
  | ({
    mode: 'EVENT';
  } & EventScheduleFieldWindow);
  takenDown:
  | ({
    mode: 'FIXED';
  } & FixedScheduleFieldWindow)
  | ({
    mode: 'EVENT';
  } & EventScheduleFieldWindow);
}
export interface GroupSize {
  minItems: number;
  maxItems: number;
}
export interface BenderTemplateConfig {
  assetType: AssetType;
  /** When true, generates all permutations of titles (if count ≤ maxGroups). Order matters for Ad creative. */
  generateAllOrderings?: boolean;
  /** Bender template config options map */
  config?: Record<string, object>;
  /** Link configuration for URL generation */
  destinationLinkConfig?:
  | DetailPageLinkConfig
  | HomePageLinkConfig
  | LandingPageLinkConfig;
  /** Per-live-status schedule windows, keyed by live status (e.g. LIVE_NOW) */
  scheduleWindows?: Record<string, TemplateScheduleWindow>;
  templateId: string;
  templateConfigId?: string;
  templateType?: 'SINGLE_TITLE' | 'MULTI_TITLE';
  groupingStrategy?: 'DEFAULT' | 'ATTRIBUTE';
  groupByAttribute?: 'channels';
  groupSize?: GroupSize;
}
export interface ProductStrategy {
  name?: string;
  gtiList?: string[];
  country: Country;
  locales: Locale[];
  businessLine?: BusinessLine;
  /** Publisher-specific catalog configuration override */
  customCatalogConfig?:
  | MetaCatalogConfig
  | TikTokCatalogConfig
  | AdspCatalogConfig;
  productStrategyId?: string;
  pageId?: string;
  strategyId?: string;
  benderTemplates: BenderTemplateConfig[];
  productSetId?: string;
  productSetName?: string;
  groupId?: string;
}
export interface PlacementWithoutStrategy {
  placementType:
  | 'FACEBOOK_FEED'
  | 'FACEBOOK_VIDEO_FEEDS'
  | 'FACEBOOK_STORY'
  | 'FACEBOOK_SEARCH'
  | 'FACEBOOK_INSTREAM_VIDEO'
  | 'FACEBOOK_REELS'
  | 'FACEBOOK_PROFILE_FEED'
  | 'INSTAGRAM_STREAM'
  | 'INSTAGRAM_STORY'
  | 'INSTAGRAM_EXPLORE'
  | 'INSTAGRAM_EXPLORE_HOME'
  | 'INSTAGRAM_REELS'
  | 'INSTAGRAM_PROFILE_FEED'
  | 'INSTAGRAM_SEARCH'
  | 'TIKTOK'
  | 'TIKTOK_GLOBAL_APP_BUNDLE'
  | 'TIKTOK_PANGLE'
  | 'GOOGLE_YOUTUBE_IN_FEED'
  | 'GOOGLE_YOUTUBE_IN_STREAM'
  | 'GOOGLE_YOUTUBE_SHORTS'
  | 'GOOGLE_DISCOVER'
  | 'GOOGLE_GMAIL'
  | 'ADSP_DISPLAY_DESKTOP_WEB'
  | 'ADSP_DISPLAY_MOBILE_APP'
  | 'ADSP_ONLINE_VIDEO'
  | 'ADSP_STREAMING_TV';
  benderTemplateId: string;
}
export interface TargetingStrategy {
  locale: Locale;
  country: Country;
  strategyId: string;
  devices: Array<'ANDROID' | 'IOS' | 'WEB'>;
  placement: PlacementWithoutStrategy;
  targetingServiceId: string;
}
export interface Map {}
export interface Ad {
  adId?: string;
  name?: string;
  publisherId?: string;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED' | 'PAUSED';
  description?: string;
  callToAction?: string;
  refTag: string;
  assets: string[];
  additionalFields: Map;
}
export interface AdSet {
  adSetId: string;
  name?: string;
  publisherId?: string;
  productSetId?: string;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED' | 'PAUSED';
  optimizationGoal:
  | 'NONE'
  | 'APP_INSTALLS'
  | 'AD_RECALL_LIFT'
  | 'ENGAGED_USERS'
  | 'EVENT_RESPONSES'
  | 'IMPRESSIONS'
  | 'LEAD_GENERATION'
  | 'QUALITY_LEAD'
  | 'LINK_CLICKS'
  | 'OFFSITE_CONVERSIONS'
  | 'PAGE_LIKES'
  | 'POST_ENGAGEMENT'
  | 'QUALITY_CALL'
  | 'REACH'
  | 'LANDING_PAGE_VIEWS'
  | 'VISIT_INSTAGRAM_PROFILE'
  | 'VALUE'
  | 'THRUPLAY'
  | 'DERIVED_EVENTS'
  | 'APP_INSTALLS_AND_OFFSITE_CONVERSIONS'
  | 'CONVERSATIONS'
  | 'IN_APP_VALUE'
  | 'MESSAGING_PURCHASE_CONVERSION'
  | 'MESSAGING_APPOINTMENT_CONVERSION'
  | 'SUBSCRIBERS'
  | 'REMINDERS_SET'
  | 'MEANINGFUL_CALL_ATTEMPT';
  budgetStrategy?: BudgetStrategy;
  targetingStrategy: TargetingStrategy;
  ads?: Ad[];
}
export interface DestinationUrlConfig {
  type: 'DETAIL' | 'HOME' | 'LANDING';
}
export interface GoogleBudget {
  /** Budget in the ACCOUNT CURRENCY — do NOT assume USD. The currency is `desiredState.publisherAccountConfig.google.currency`; display it with `publisherAccountConfig.google.currencySymbol` (e.g. "$", "€", "R$"). "5" means 5 units of that currency. Interpreted by period: DAILY = daily spend, LIFETIME = total campaign spend (requires campaign endDate). Minimum daily budget: clients MUST enforce at least `publisherAccountConfig.google.minDailyBudget` (the per-currency floor) and reject a lower daily amount before submitting. Google is the final authority and re-validates the exact per-currency minimum at CAMPAIGN_SETUP (else BUDGET_BELOW_PER_DAY_MINIMUM). */
  amount?: string;
  /** Budget period. Required for approval. Display: DAILY = "Daily Budget" (recurring daily spend), LIFETIME = "Lifetime Budget" (total spend across campaign duration, campaign endDate required). A single `amount` field is used for both; period decides its meaning. */
  period?: 'DAILY' | 'LIFETIME';
}
export interface TargetSpendStrategy {
  /** TARGET_SPEND = "Maximize Clicks" (get as many clicks as possible within budget, no CPC control). */
  type: 'TARGET_SPEND';
}
export interface TargetCpcStrategy {
  /** TARGET_CPC = "Maximize Clicks (with target CPC)" (target a specific average cost per click). */
  type: 'TARGET_CPC';
  /** Target cost per click in the account currency (do not assume USD). "2" means 2 in the account currency. Minimum 0.01. */
  targetCpc: string;
}
export interface TargetCpaStrategy {
  /** TARGET_CPA = target cost per acquisition. NOT YET SUPPORTED — rejected at approval. */
  type: 'TARGET_CPA';
  /** Target cost per acquisition in the account currency (do not assume USD). "10" means 10 in the account currency. */
  targetCpa: string;
}
export interface MaximizeConversionsStrategy {
  /** MAXIMIZE_CONVERSIONS = maximize conversions, optional target CPA ceiling. NOT YET SUPPORTED — rejected at approval. */
  type: 'MAXIMIZE_CONVERSIONS';
  /** Optional target CPA ceiling in the account currency (do not assume USD). "10" means 10 in the account currency. */
  targetCpa?: string;
}
export interface MultiAssetAdConfig {
  /** Ad format type. Display: MULTI_ASSET = "Image Ads", VIDEO_RESPONSIVE = "Video Ads". */
  adType: 'MULTI_ASSET' | 'VIDEO_RESPONSIVE';
  /** Choose one headline for this ad group. Show these options to the user: 1. "Watch {title} on {brand}" (default), 2. Custom text (supports {title}, {brand}, and {country} macros, max 40 chars after resolution). If not provided, "Watch {title} on {brand}" is used. Falls back to "{title}" if resolved text exceeds 40 chars. */
  headlines?: string[];
  /** Choose one description for this ad group. Show these options to the user: 1. "Available now on {brand}" (default), 2. "Trending now in {country}", 3. Custom text (supports {title}, {brand}, and {country} macros, max 90 chars after resolution). If not provided, "Available now on {brand}" is used. */
  descriptions?: string[];
  /** Call-to-action text */
  callToActionText?: string;
  /** Number of images to include per ad */
  numImagesPerAd?: number;
}
export interface GoogleAdGroupConfig {
  /** Internal identifier for this ad group configuration */
  adGroupConfigId?: string;
  /** Leela product strategy ID for this ad group */
  productStrategyId: string;
  /** Display name for the ad group in Google Ads */
  name: string;
  /** Google surfaces to show ads on. Required for approval (at least one). Must match adType: MULTI_ASSET → DISCOVER, YOUTUBE_IN_FEED, GMAIL; VIDEO_RESPONSIVE → YOUTUBE_IN_FEED, YOUTUBE_IN_STREAM, YOUTUBE_SHORTS, DISCOVER. Gmail serves image ads only and bills on the first teaser click. Display names: YOUTUBE_IN_FEED = "YouTube In-Feed", YOUTUBE_IN_STREAM = "YouTube In-Stream", YOUTUBE_SHORTS = "YouTube Shorts", DISCOVER = "Discover Feed", GMAIL = "Gmail". */
  enabledChannels?: Array< | 'YOUTUBE_IN_FEED'
  | 'YOUTUBE_IN_STREAM'
  | 'YOUTUBE_SHORTS'
  | 'DISCOVER'
  | 'GMAIL'>;
  /** Target languages for this ad group. Google shows ads to users whose browser or device language matches. At least one required. Never auto-select — always ask the user. Display: ARABIC = "Arabic", BENGALI = "Bengali", CHINESE_SIMPLIFIED = "Chinese (Simplified)", CHINESE_TRADITIONAL = "Chinese (Traditional)", CZECH = "Czech", DANISH = "Danish", DUTCH = "Dutch", ENGLISH = "English", FINNISH = "Finnish", FRENCH = "French", GERMAN = "German", HEBREW = "Hebrew", HINDI = "Hindi", HUNGARIAN = "Hungarian", INDONESIAN = "Indonesian", ITALIAN = "Italian", JAPANESE = "Japanese", KANNADA = "Kannada", KOREAN = "Korean", MALAY = "Malay", MALAYALAM = "Malayalam", MARATHI = "Marathi", NORWEGIAN = "Norwegian", POLISH = "Polish", PORTUGUESE = "Portuguese", ROMANIAN = "Romanian", SPANISH = "Spanish", SWEDISH = "Swedish", TAMIL = "Tamil", TELUGU = "Telugu", THAI = "Thai", TURKISH = "Turkish". Select languages relevant to the campaign's target market. */
  targetLanguages?: Array< | 'ARABIC'
  | 'BENGALI'
  | 'CHINESE_SIMPLIFIED'
  | 'CHINESE_TRADITIONAL'
  | 'CZECH'
  | 'DANISH'
  | 'DUTCH'
  | 'ENGLISH'
  | 'FINNISH'
  | 'FRENCH'
  | 'GERMAN'
  | 'HEBREW'
  | 'HINDI'
  | 'HUNGARIAN'
  | 'INDONESIAN'
  | 'ITALIAN'
  | 'JAPANESE'
  | 'KANNADA'
  | 'KOREAN'
  | 'MALAY'
  | 'MALAYALAM'
  | 'MARATHI'
  | 'NORWEGIAN'
  | 'POLISH'
  | 'PORTUGUESE'
  | 'ROMANIAN'
  | 'SPANISH'
  | 'SWEDISH'
  | 'TAMIL'
  | 'TELUGU'
  | 'THAI'
  | 'TURKISH'>;
  /** Allow Google to expand audience beyond targeting signals */
  optimizedTargetingEnabled?: boolean;
  /** Creative configurations for ads in this ad group */
  adConfigs: MultiAssetAdConfig[];
  /** Google Ads ad group ID (execution-populated) */
  adGroupId?: string;
  /** Google Ads ad group resource name (execution-populated) */
  adGroupResourceName?: string;
  /** Google Ads ad ID (execution-populated) */
  adId?: string;
  /** Google Ads ad resource name (execution-populated) */
  adResourceName?: string;
}
export interface GooglePublisherCampaignConfig {
  /** Daily budget configuration */
  budget?: GoogleBudget;
  /** Bidding strategy (discriminated by `type`). Required for approval. TARGET_SPEND = "Maximize Clicks" (no CPC control). TARGET_CPC = "Maximize Clicks (with target CPC)" — carries a `targetCpc` in account currency. TARGET_CPA = target cost per acquisition — NOT YET SUPPORTED, rejected at approval; do not use. MAXIMIZE_CONVERSIONS = maximize conversions (optional targetCpa ceiling) — NOT YET SUPPORTED, rejected at approval; do not use. */
  biddingStrategy?:
  | ({
    type: 'TARGET_SPEND';
  } & TargetSpendStrategy)
  | ({
    type: 'TARGET_CPC';
  } & TargetCpcStrategy)
  | ({
    type: 'TARGET_CPA';
  } & TargetCpaStrategy)
  | ({
    type: 'MAXIMIZE_CONVERSIONS';
  } & MaximizeConversionsStrategy);
  /** Campaign start date (YYYY-MM-DD). Required for approval. Must be today or a future date. */
  startDate?: string;
  /** Campaign end date (YYYY-MM-DD). Required for approval. Must be after startDate. */
  endDate?: string;
  /** Devices to target. If not set, all devices are targeted. Display: MOBILE = "Mobile phones", DESKTOP = "Computers", TABLET = "Tablets", CONNECTED_TV = "TV screens". Select the devices where your audience is most likely to engage. */
  targetDevices?: Array<'MOBILE' | 'DESKTOP' | 'TABLET' | 'CONNECTED_TV'>;
  /** Default call-to-action text for all ads */
  defaultCallToAction?: string;
  /** Tracking URL template for click attribution */
  trackingUrlTemplate?: string;
  /** Final URL suffix appended to landing page URLs */
  finalUrlSuffix?: string;
  /** Ad group configurations. Required for approval (at least one). Each ad group must have: name, productStrategyId, enabledChannels (at least one), and adConfigs (at least one). Channel/adType compatibility: MULTI_ASSET → DISCOVER, YOUTUBE_IN_FEED, GMAIL; VIDEO_RESPONSIVE → YOUTUBE_IN_FEED, YOUTUBE_IN_STREAM, YOUTUBE_SHORTS, DISCOVER. */
  adGroups?: GoogleAdGroupConfig[];
  /** Google Ads campaign ID (execution-populated) */
  campaignId?: string;
  /** Google Ads campaign resource name (execution-populated) */
  campaignResourceName?: string;
  /** Google Ads customer account ID (execution-populated) */
  customerId?: string;
  /** Google Ads budget resource name (execution-populated) */
  budgetResourceName?: string;
  /** ISO timestamp of last successful sync with Google Ads */
  lastSyncedAt?: string;
}
export interface CampaignListItemDto {
  programName: string;
  publishStatusSummary: PublishStatusSummaryDto;
  activePlanStatus: CampaignPlanStatus;
  campaignId: string;
  programId: string;
  publisher: 'meta' | 'tiktok' | 'google' | 'adsp';
  configIds?: string[];
  exclusionListId?: string;
  notificationRecipients?: string[];
  publisherAccountConfig?: PublisherAccountConfigDto;
  createdAt?: object;
  updatedAt?: object;
  name?: string;
  campaignObjective?:
  | 'APP_INSTALLS'
  | 'BRAND_AWARENESS'
  | 'CONVERSIONS'
  | 'EVENT_RESPONSES'
  | 'LEAD_GENERATION'
  | 'LINK_CLICKS'
  | 'LOCAL_AWARENESS'
  | 'MESSAGES'
  | 'OFFER_CLAIMS'
  | 'OUTCOME_APP_PROMOTION'
  | 'OUTCOME_AWARENESS'
  | 'OUTCOME_ENGAGEMENT'
  | 'OUTCOME_LEADS'
  | 'OUTCOME_SALES'
  | 'OUTCOME_TRAFFIC'
  | 'PAGE_LIKES'
  | 'POST_ENGAGEMENT'
  | 'PRODUCT_CATALOG_SALES'
  | 'REACH'
  | 'STORE_VISITS'
  | 'VIDEO_VIEWS';
  optimizationGoal?:
  | 'NONE'
  | 'APP_INSTALLS'
  | 'AD_RECALL_LIFT'
  | 'ENGAGED_USERS'
  | 'EVENT_RESPONSES'
  | 'IMPRESSIONS'
  | 'LEAD_GENERATION'
  | 'QUALITY_LEAD'
  | 'LINK_CLICKS'
  | 'OFFSITE_CONVERSIONS'
  | 'PAGE_LIKES'
  | 'POST_ENGAGEMENT'
  | 'QUALITY_CALL'
  | 'REACH'
  | 'LANDING_PAGE_VIEWS'
  | 'VISIT_INSTAGRAM_PROFILE'
  | 'VALUE'
  | 'THRUPLAY'
  | 'DERIVED_EVENTS'
  | 'APP_INSTALLS_AND_OFFSITE_CONVERSIONS'
  | 'CONVERSATIONS'
  | 'IN_APP_VALUE'
  | 'MESSAGING_PURCHASE_CONVERSION'
  | 'MESSAGING_APPOINTMENT_CONVERSION'
  | 'SUBSCRIBERS'
  | 'REMINDERS_SET'
  | 'MEANINGFUL_CALL_ATTEMPT';
  budgetStrategy?: BudgetStrategy;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED' | 'PAUSED' | 'SUSPENDED';
  placements: Placement[];
  productStrategies: ProductStrategy[];
  adSets: AdSet[];
  destinationUrlConfig: DestinationUrlConfig;
  publisherCampaignConfig?: GooglePublisherCampaignConfig;
}
export type CampaignQueue =
  | 'publishFailed'
  | 'assetBlockers'
  | 'awaitingReview'
  | 'publishingNow';
export interface CampaignQueueCountsDto {
  /** Campaigns with an in-stock publisher entity that failed to sync */
  publishFailed: number;
  /** Campaigns with at least one blocked asset */
  assetBlockers: number;
  /** Campaigns with at least one asset pending review */
  awaitingReview: number;
  /** Campaigns with an in-stock publisher entity being published */
  publishingNow: number;
}
export interface CampaignDesiredStateDto {
  campaignId: string;
  programId: string;
  publisher: 'meta' | 'tiktok' | 'google' | 'adsp';
  configIds?: string[];
  exclusionListId?: string;
  notificationRecipients?: string[];
  publisherAccountConfig?: PublisherAccountConfigDto;
  createdAt?: object;
  updatedAt?: object;
  name?: string;
  campaignObjective?:
  | 'APP_INSTALLS'
  | 'BRAND_AWARENESS'
  | 'CONVERSIONS'
  | 'EVENT_RESPONSES'
  | 'LEAD_GENERATION'
  | 'LINK_CLICKS'
  | 'LOCAL_AWARENESS'
  | 'MESSAGES'
  | 'OFFER_CLAIMS'
  | 'OUTCOME_APP_PROMOTION'
  | 'OUTCOME_AWARENESS'
  | 'OUTCOME_ENGAGEMENT'
  | 'OUTCOME_LEADS'
  | 'OUTCOME_SALES'
  | 'OUTCOME_TRAFFIC'
  | 'PAGE_LIKES'
  | 'POST_ENGAGEMENT'
  | 'PRODUCT_CATALOG_SALES'
  | 'REACH'
  | 'STORE_VISITS'
  | 'VIDEO_VIEWS';
  optimizationGoal?:
  | 'NONE'
  | 'APP_INSTALLS'
  | 'AD_RECALL_LIFT'
  | 'ENGAGED_USERS'
  | 'EVENT_RESPONSES'
  | 'IMPRESSIONS'
  | 'LEAD_GENERATION'
  | 'QUALITY_LEAD'
  | 'LINK_CLICKS'
  | 'OFFSITE_CONVERSIONS'
  | 'PAGE_LIKES'
  | 'POST_ENGAGEMENT'
  | 'QUALITY_CALL'
  | 'REACH'
  | 'LANDING_PAGE_VIEWS'
  | 'VISIT_INSTAGRAM_PROFILE'
  | 'VALUE'
  | 'THRUPLAY'
  | 'DERIVED_EVENTS'
  | 'APP_INSTALLS_AND_OFFSITE_CONVERSIONS'
  | 'CONVERSATIONS'
  | 'IN_APP_VALUE'
  | 'MESSAGING_PURCHASE_CONVERSION'
  | 'MESSAGING_APPOINTMENT_CONVERSION'
  | 'SUBSCRIBERS'
  | 'REMINDERS_SET'
  | 'MEANINGFUL_CALL_ATTEMPT';
  budgetStrategy?: BudgetStrategy;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED' | 'PAUSED' | 'SUSPENDED';
  placements: Placement[];
  productStrategies: ProductStrategy[];
  adSets: AdSet[];
  destinationUrlConfig: DestinationUrlConfig;
  publisherCampaignConfig?: GooglePublisherCampaignConfig;
}
export interface CreateCampaignDto {
  programId: string;
  publisher: 'meta' | 'tiktok' | 'google' | 'adsp';
  configIds?: string[];
  exclusionListId?: string;
  partnerId?: string;
  notificationRecipients?: string[];
  productStrategies: ProductStrategy[];
  name?: string;
  campaignObjective?:
  | 'APP_INSTALLS'
  | 'BRAND_AWARENESS'
  | 'CONVERSIONS'
  | 'EVENT_RESPONSES'
  | 'LEAD_GENERATION'
  | 'LINK_CLICKS'
  | 'LOCAL_AWARENESS'
  | 'MESSAGES'
  | 'OFFER_CLAIMS'
  | 'OUTCOME_APP_PROMOTION'
  | 'OUTCOME_AWARENESS'
  | 'OUTCOME_ENGAGEMENT'
  | 'OUTCOME_LEADS'
  | 'OUTCOME_SALES'
  | 'OUTCOME_TRAFFIC'
  | 'PAGE_LIKES'
  | 'POST_ENGAGEMENT'
  | 'PRODUCT_CATALOG_SALES'
  | 'REACH'
  | 'STORE_VISITS'
  | 'VIDEO_VIEWS';
  optimizationGoal?:
  | 'NONE'
  | 'APP_INSTALLS'
  | 'AD_RECALL_LIFT'
  | 'ENGAGED_USERS'
  | 'EVENT_RESPONSES'
  | 'IMPRESSIONS'
  | 'LEAD_GENERATION'
  | 'QUALITY_LEAD'
  | 'LINK_CLICKS'
  | 'OFFSITE_CONVERSIONS'
  | 'PAGE_LIKES'
  | 'POST_ENGAGEMENT'
  | 'QUALITY_CALL'
  | 'REACH'
  | 'LANDING_PAGE_VIEWS'
  | 'VISIT_INSTAGRAM_PROFILE'
  | 'VALUE'
  | 'THRUPLAY'
  | 'DERIVED_EVENTS'
  | 'APP_INSTALLS_AND_OFFSITE_CONVERSIONS'
  | 'CONVERSATIONS'
  | 'IN_APP_VALUE'
  | 'MESSAGING_PURCHASE_CONVERSION'
  | 'MESSAGING_APPOINTMENT_CONVERSION'
  | 'SUBSCRIBERS'
  | 'REMINDERS_SET'
  | 'MEANINGFUL_CALL_ATTEMPT';
  budgetStrategy?: BudgetStrategy;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED' | 'PAUSED' | 'SUSPENDED';
  placements: Placement[];
  destinationUrlConfig: DestinationUrlConfig;
  publisherCampaignConfig?: GooglePublisherCampaignConfig;
}
export interface UpdateCampaignDto {
  priority: 'IMMEDIATE' | 'NORMAL' | 'SCHEDULED';
  adSets?: AdSet[];
  campaignObjective?:
  | 'APP_INSTALLS'
  | 'BRAND_AWARENESS'
  | 'CONVERSIONS'
  | 'EVENT_RESPONSES'
  | 'LEAD_GENERATION'
  | 'LINK_CLICKS'
  | 'LOCAL_AWARENESS'
  | 'MESSAGES'
  | 'OFFER_CLAIMS'
  | 'OUTCOME_APP_PROMOTION'
  | 'OUTCOME_AWARENESS'
  | 'OUTCOME_ENGAGEMENT'
  | 'OUTCOME_LEADS'
  | 'OUTCOME_SALES'
  | 'OUTCOME_TRAFFIC'
  | 'PAGE_LIKES'
  | 'POST_ENGAGEMENT'
  | 'PRODUCT_CATALOG_SALES'
  | 'REACH'
  | 'STORE_VISITS'
  | 'VIDEO_VIEWS';
  optimizationGoal?:
  | 'NONE'
  | 'APP_INSTALLS'
  | 'AD_RECALL_LIFT'
  | 'ENGAGED_USERS'
  | 'EVENT_RESPONSES'
  | 'IMPRESSIONS'
  | 'LEAD_GENERATION'
  | 'QUALITY_LEAD'
  | 'LINK_CLICKS'
  | 'OFFSITE_CONVERSIONS'
  | 'PAGE_LIKES'
  | 'POST_ENGAGEMENT'
  | 'QUALITY_CALL'
  | 'REACH'
  | 'LANDING_PAGE_VIEWS'
  | 'VISIT_INSTAGRAM_PROFILE'
  | 'VALUE'
  | 'THRUPLAY'
  | 'DERIVED_EVENTS'
  | 'APP_INSTALLS_AND_OFFSITE_CONVERSIONS'
  | 'CONVERSATIONS'
  | 'IN_APP_VALUE'
  | 'MESSAGING_PURCHASE_CONVERSION'
  | 'MESSAGING_APPOINTMENT_CONVERSION'
  | 'SUBSCRIBERS'
  | 'REMINDERS_SET'
  | 'MEANINGFUL_CALL_ATTEMPT';
  budgetStrategy?: BudgetStrategy;
  status?: 'ACTIVE' | 'ARCHIVED' | 'DELETED' | 'PAUSED' | 'SUSPENDED';
  placements?: Placement[];
  productStrategies?: ProductStrategy[];
  destinationUrlConfig?: DestinationUrlConfig;
  programId?: string;
  publisher?: 'meta' | 'tiktok' | 'google' | 'adsp';
  configIds?: string[];
  exclusionListId?: string;
  notificationRecipients?: string[];
}
export type ProductInventoryStatus = 'in stock' | 'out of stock';
export interface Product {
  inventoryStatus?: ProductInventoryStatus;
  productId: string;
  assetId: string;
}
export interface ProductSet {
  productSetId: string;
  catalogCsvUrl?: string;
  dataFeedId?: string;
  products: Product[];
}
export interface PublishedProductStrategy {
  name?: string;
  gtiList?: string[];
  country: Country;
  locales: Locale[];
  businessLine?: BusinessLine;
  /** Publisher-specific catalog configuration override */
  customCatalogConfig?:
  | MetaCatalogConfig
  | TikTokCatalogConfig
  | AdspCatalogConfig;
  productStrategyId?: string;
  pageId?: string;
  strategyId?: string;
  benderTemplates: BenderTemplateConfig[];
  productSetId?: string;
  productSetName?: string;
  groupId?: string;
}
export interface CampaignPublishedStateDto {
  campaignId: string;
  programId: string;
  publisher: 'meta' | 'tiktok' | 'google' | 'adsp';
  campaignIdPublisher: string;
  adAccountId: string;
  productSets: ProductSet[];
  productStrategies: PublishedProductStrategy[];
  createdAt?: object;
  updatedAt?: object;
  name?: string;
  campaignObjective?:
  | 'APP_INSTALLS'
  | 'BRAND_AWARENESS'
  | 'CONVERSIONS'
  | 'EVENT_RESPONSES'
  | 'LEAD_GENERATION'
  | 'LINK_CLICKS'
  | 'LOCAL_AWARENESS'
  | 'MESSAGES'
  | 'OFFER_CLAIMS'
  | 'OUTCOME_APP_PROMOTION'
  | 'OUTCOME_AWARENESS'
  | 'OUTCOME_ENGAGEMENT'
  | 'OUTCOME_LEADS'
  | 'OUTCOME_SALES'
  | 'OUTCOME_TRAFFIC'
  | 'PAGE_LIKES'
  | 'POST_ENGAGEMENT'
  | 'PRODUCT_CATALOG_SALES'
  | 'REACH'
  | 'STORE_VISITS'
  | 'VIDEO_VIEWS';
  optimizationGoal?:
  | 'NONE'
  | 'APP_INSTALLS'
  | 'AD_RECALL_LIFT'
  | 'ENGAGED_USERS'
  | 'EVENT_RESPONSES'
  | 'IMPRESSIONS'
  | 'LEAD_GENERATION'
  | 'QUALITY_LEAD'
  | 'LINK_CLICKS'
  | 'OFFSITE_CONVERSIONS'
  | 'PAGE_LIKES'
  | 'POST_ENGAGEMENT'
  | 'QUALITY_CALL'
  | 'REACH'
  | 'LANDING_PAGE_VIEWS'
  | 'VISIT_INSTAGRAM_PROFILE'
  | 'VALUE'
  | 'THRUPLAY'
  | 'DERIVED_EVENTS'
  | 'APP_INSTALLS_AND_OFFSITE_CONVERSIONS'
  | 'CONVERSATIONS'
  | 'IN_APP_VALUE'
  | 'MESSAGING_PURCHASE_CONVERSION'
  | 'MESSAGING_APPOINTMENT_CONVERSION'
  | 'SUBSCRIBERS'
  | 'REMINDERS_SET'
  | 'MEANINGFUL_CALL_ATTEMPT';
  budgetStrategy?: BudgetStrategy;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED' | 'PAUSED' | 'SUSPENDED';
  placements: Placement[];
  adSets: AdSet[];
  destinationUrlConfig: DestinationUrlConfig;
  publisherCampaignConfig?: GooglePublisherCampaignConfig;
}
export interface UpdateCampaignPublishedStateDto {
  programId: string;
  publisher: 'meta' | 'tiktok' | 'google' | 'adsp';
  name?: string;
  campaignObjective?:
  | 'APP_INSTALLS'
  | 'BRAND_AWARENESS'
  | 'CONVERSIONS'
  | 'EVENT_RESPONSES'
  | 'LEAD_GENERATION'
  | 'LINK_CLICKS'
  | 'LOCAL_AWARENESS'
  | 'MESSAGES'
  | 'OFFER_CLAIMS'
  | 'OUTCOME_APP_PROMOTION'
  | 'OUTCOME_AWARENESS'
  | 'OUTCOME_ENGAGEMENT'
  | 'OUTCOME_LEADS'
  | 'OUTCOME_SALES'
  | 'OUTCOME_TRAFFIC'
  | 'PAGE_LIKES'
  | 'POST_ENGAGEMENT'
  | 'PRODUCT_CATALOG_SALES'
  | 'REACH'
  | 'STORE_VISITS'
  | 'VIDEO_VIEWS';
  optimizationGoal?:
  | 'NONE'
  | 'APP_INSTALLS'
  | 'AD_RECALL_LIFT'
  | 'ENGAGED_USERS'
  | 'EVENT_RESPONSES'
  | 'IMPRESSIONS'
  | 'LEAD_GENERATION'
  | 'QUALITY_LEAD'
  | 'LINK_CLICKS'
  | 'OFFSITE_CONVERSIONS'
  | 'PAGE_LIKES'
  | 'POST_ENGAGEMENT'
  | 'QUALITY_CALL'
  | 'REACH'
  | 'LANDING_PAGE_VIEWS'
  | 'VISIT_INSTAGRAM_PROFILE'
  | 'VALUE'
  | 'THRUPLAY'
  | 'DERIVED_EVENTS'
  | 'APP_INSTALLS_AND_OFFSITE_CONVERSIONS'
  | 'CONVERSATIONS'
  | 'IN_APP_VALUE'
  | 'MESSAGING_PURCHASE_CONVERSION'
  | 'MESSAGING_APPOINTMENT_CONVERSION'
  | 'SUBSCRIBERS'
  | 'REMINDERS_SET'
  | 'MEANINGFUL_CALL_ATTEMPT';
  budgetStrategy?: BudgetStrategy;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED' | 'PAUSED' | 'SUSPENDED';
  placements: Placement[];
  productStrategies: PublishedProductStrategy[];
  adSets: AdSet[];
  destinationUrlConfig: DestinationUrlConfig;
  publisherCampaignConfig?: GooglePublisherCampaignConfig;
  campaignIdPublisher: string;
  adAccountId: string;
  productSets: ProductSet[];
}
export interface NotificationRecipientEligibilityDto {
  eligible: string[];
  ineligible: string[];
}
export interface ValidateNotificationRecipientsDto {
  emails: string[];
}
export type Publisher = 'meta' | 'tiktok' | 'google' | 'adsp';
export type CreationSource = 'USER' | 'SYSTEM' | 'ADMIN';
export interface CampaignConfigurationDto {
  supportedBusinessLines?: BusinessLine[];
  supportedCountries?: Country[];
  supportedPublishers?: Publisher[];
  /** BusinessUnit._id for a BU-owned PUBLISHER_ACCOUNT or GTI_BLOCKLIST; publisher accounts may set it once on create or update and cannot change it afterward */
  businessUnitId?: string;
  /** Partner._id of the one agency allowed to use a PUBLISHER_ACCOUNT; it must belong to the config businessUnitId, and null on update clears it. Absent means internal-only. Ignored for every other config type (a GTI_BLOCKLIST partner is server-derived) */
  partnerId?: string | null;
  source?: CreationSource;
  name?: string;
  config: object;
  owner?: string;
  userSharedList?: string[];
  id: string;
  createdAt?: object;
  updatedAt?: object;
  campaignCount?: number;
}
export interface CreateCampaignConfigurationDto {
  supportedBusinessLines?: BusinessLine[];
  supportedCountries?: Country[];
  supportedPublishers?: Publisher[];
  /** BusinessUnit._id for a BU-owned PUBLISHER_ACCOUNT or GTI_BLOCKLIST; publisher accounts may set it once on create or update and cannot change it afterward */
  businessUnitId?: string;
  /** Partner._id of the one agency allowed to use a PUBLISHER_ACCOUNT; it must belong to the config businessUnitId, and null on update clears it. Absent means internal-only. Ignored for every other config type (a GTI_BLOCKLIST partner is server-derived) */
  partnerId?: string | null;
  source?: CreationSource;
  name?: string;
  config: object;
  owner?: string;
  userSharedList?: string[];
}
export interface UpdateCampaignConfigurationDto {
  supportedBusinessLines?: BusinessLine[];
  supportedCountries?: Country[];
  supportedPublishers?: Publisher[];
  /** BusinessUnit._id for a BU-owned PUBLISHER_ACCOUNT or GTI_BLOCKLIST; publisher accounts may set it once on create or update and cannot change it afterward */
  businessUnitId?: string;
  /** Partner._id of the one agency allowed to use a PUBLISHER_ACCOUNT; it must belong to the config businessUnitId, and null on update clears it. Absent means internal-only. Ignored for every other config type (a GTI_BLOCKLIST partner is server-derived) */
  partnerId?: string | null;
  source?: CreationSource;
  config?: object;
  name?: string;
  owner?: string;
  userSharedList?: string[];
}
export interface BusinessUnitDto {
  id: string;
  name: string;
  admins: string[];
  countries: Array< | 'AE'
  | 'AR'
  | 'AU'
  | 'BE'
  | 'BO'
  | 'BR'
  | 'CA'
  | 'CH'
  | 'CL'
  | 'CO'
  | 'CR'
  | 'CZ'
  | 'DE'
  | 'DK'
  | 'DO'
  | 'EC'
  | 'EG'
  | 'ES'
  | 'FI'
  | 'FR'
  | 'GB'
  | 'GT'
  | 'HN'
  | 'HU'
  | 'ID'
  | 'IE'
  | 'IN'
  | 'IT'
  | 'JP'
  | 'MX'
  | 'MY'
  | 'NG'
  | 'NI'
  | 'NL'
  | 'NO'
  | 'NZ'
  | 'PA'
  | 'PE'
  | 'PH'
  | 'PL'
  | 'PR'
  | 'PT'
  | 'PY'
  | 'RO'
  | 'SA'
  | 'SE'
  | 'SG'
  | 'SV'
  | 'TH'
  | 'TR'
  | 'US'
  | 'UY'
  | 'VE'
  | 'ZA'>;
}
export interface CreateBusinessUnitDto {
  name: string;
  admins: string[];
  countries: Array< | 'AE'
  | 'AR'
  | 'AU'
  | 'BE'
  | 'BO'
  | 'BR'
  | 'CA'
  | 'CH'
  | 'CL'
  | 'CO'
  | 'CR'
  | 'CZ'
  | 'DE'
  | 'DK'
  | 'DO'
  | 'EC'
  | 'EG'
  | 'ES'
  | 'FI'
  | 'FR'
  | 'GB'
  | 'GT'
  | 'HN'
  | 'HU'
  | 'ID'
  | 'IE'
  | 'IN'
  | 'IT'
  | 'JP'
  | 'MX'
  | 'MY'
  | 'NG'
  | 'NI'
  | 'NL'
  | 'NO'
  | 'NZ'
  | 'PA'
  | 'PE'
  | 'PH'
  | 'PL'
  | 'PR'
  | 'PT'
  | 'PY'
  | 'RO'
  | 'SA'
  | 'SE'
  | 'SG'
  | 'SV'
  | 'TH'
  | 'TR'
  | 'US'
  | 'UY'
  | 'VE'
  | 'ZA'>;
}
export interface UpdateBusinessUnitDto {
  name?: string;
  admins?: string[];
  countries?: Array< | 'AE'
  | 'AR'
  | 'AU'
  | 'BE'
  | 'BO'
  | 'BR'
  | 'CA'
  | 'CH'
  | 'CL'
  | 'CO'
  | 'CR'
  | 'CZ'
  | 'DE'
  | 'DK'
  | 'DO'
  | 'EC'
  | 'EG'
  | 'ES'
  | 'FI'
  | 'FR'
  | 'GB'
  | 'GT'
  | 'HN'
  | 'HU'
  | 'ID'
  | 'IE'
  | 'IN'
  | 'IT'
  | 'JP'
  | 'MX'
  | 'MY'
  | 'NG'
  | 'NI'
  | 'NL'
  | 'NO'
  | 'NZ'
  | 'PA'
  | 'PE'
  | 'PH'
  | 'PL'
  | 'PR'
  | 'PT'
  | 'PY'
  | 'RO'
  | 'SA'
  | 'SE'
  | 'SG'
  | 'SV'
  | 'TH'
  | 'TR'
  | 'US'
  | 'UY'
  | 'VE'
  | 'ZA'>;
}
export interface AdministeredBusinessUnitDto {
  id: string;
  name: string;
  countries: Array< | 'AE'
  | 'AR'
  | 'AU'
  | 'BE'
  | 'BO'
  | 'BR'
  | 'CA'
  | 'CH'
  | 'CL'
  | 'CO'
  | 'CR'
  | 'CZ'
  | 'DE'
  | 'DK'
  | 'DO'
  | 'EC'
  | 'EG'
  | 'ES'
  | 'FI'
  | 'FR'
  | 'GB'
  | 'GT'
  | 'HN'
  | 'HU'
  | 'ID'
  | 'IE'
  | 'IN'
  | 'IT'
  | 'JP'
  | 'MX'
  | 'MY'
  | 'NG'
  | 'NI'
  | 'NL'
  | 'NO'
  | 'NZ'
  | 'PA'
  | 'PE'
  | 'PH'
  | 'PL'
  | 'PR'
  | 'PT'
  | 'PY'
  | 'RO'
  | 'SA'
  | 'SE'
  | 'SG'
  | 'SV'
  | 'TH'
  | 'TR'
  | 'US'
  | 'UY'
  | 'VE'
  | 'ZA'>;
}
export interface PermissionAssignmentDto {
  businessUnitId: string;
  groupName: string;
  role:
  | 'campaignLead'
  | 'campaignManager'
  | 'analyst'
  | 'agencyOperator'
  | 'agencyViewer';
  countries: Array< | 'AE'
  | 'AR'
  | 'AU'
  | 'BE'
  | 'BO'
  | 'BR'
  | 'CA'
  | 'CH'
  | 'CL'
  | 'CO'
  | 'CR'
  | 'CZ'
  | 'DE'
  | 'DK'
  | 'DO'
  | 'EC'
  | 'EG'
  | 'ES'
  | 'FI'
  | 'FR'
  | 'GB'
  | 'GT'
  | 'HN'
  | 'HU'
  | 'ID'
  | 'IE'
  | 'IN'
  | 'IT'
  | 'JP'
  | 'MX'
  | 'MY'
  | 'NG'
  | 'NI'
  | 'NL'
  | 'NO'
  | 'NZ'
  | 'PA'
  | 'PE'
  | 'PH'
  | 'PL'
  | 'PR'
  | 'PT'
  | 'PY'
  | 'RO'
  | 'SA'
  | 'SE'
  | 'SG'
  | 'SV'
  | 'TH'
  | 'TR'
  | 'US'
  | 'UY'
  | 'VE'
  | 'ZA'>;
  businessLines: Array< | 'SVOD'
  | 'TVOD'
  | 'CHANNEL'
  | 'Sports SVOD'
  | 'Sports TVOD'
  | 'Sports Channel'>;
  publishers: Array<'meta' | 'tiktok' | 'google' | 'adsp'>;
  partnerId?: string;
  dataVisibilityPolicy: 'FULL' | 'DIRECTIONAL' | 'AGENCY_RESTRICTED';
  permissions: Array< | 'VIEW_CAMPAIGNS'
  | 'MANAGE_CAMPAIGNS'
  | 'APPROVE_ASSETS'
  | 'MANAGE_BLOCKLISTS'
  | 'APPROVE_WORKFLOWS'>;
}
export interface MyPermissionsResponseDto {
  isPlatformAdmin: boolean;
  businessUnitAdminOf: AdministeredBusinessUnitDto[];
  assignments: PermissionAssignmentDto[];
}
export interface PartnerDto {
  id: string;
  name: string;
  businessUnitId: string;
  contractExpiry?: string;
}
export interface CreatePartnerDto {
  name: string;
  contractExpiry?: string;
}
export interface BusinessUnitMemberGroupDto {
  membershipId: string;
  groupId: string;
  groupName: string;
  role:
  | 'campaignLead'
  | 'campaignManager'
  | 'analyst'
  | 'agencyOperator'
  | 'agencyViewer';
  status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  expiresAt?: string;
}
export interface BusinessUnitMemberDto {
  userId: string;
  userType: 'INTERNAL' | 'EXTERNAL';
  groups: BusinessUnitMemberGroupDto[];
}
export interface UpdatePartnerDto {
  name?: string;
  contractExpiry?: string;
}
export interface PermissionGroupDto {
  id: string;
  name: string;
  businessUnitId: string;
  role:
  | 'campaignLead'
  | 'campaignManager'
  | 'analyst'
  | 'agencyOperator'
  | 'agencyViewer';
  countries: Array< | 'AE'
  | 'AR'
  | 'AU'
  | 'BE'
  | 'BO'
  | 'BR'
  | 'CA'
  | 'CH'
  | 'CL'
  | 'CO'
  | 'CR'
  | 'CZ'
  | 'DE'
  | 'DK'
  | 'DO'
  | 'EC'
  | 'EG'
  | 'ES'
  | 'FI'
  | 'FR'
  | 'GB'
  | 'GT'
  | 'HN'
  | 'HU'
  | 'ID'
  | 'IE'
  | 'IN'
  | 'IT'
  | 'JP'
  | 'MX'
  | 'MY'
  | 'NG'
  | 'NI'
  | 'NL'
  | 'NO'
  | 'NZ'
  | 'PA'
  | 'PE'
  | 'PH'
  | 'PL'
  | 'PR'
  | 'PT'
  | 'PY'
  | 'RO'
  | 'SA'
  | 'SE'
  | 'SG'
  | 'SV'
  | 'TH'
  | 'TR'
  | 'US'
  | 'UY'
  | 'VE'
  | 'ZA'>;
  businessLines: Array< | 'SVOD'
  | 'TVOD'
  | 'CHANNEL'
  | 'Sports SVOD'
  | 'Sports TVOD'
  | 'Sports Channel'>;
  publishers: Array<'meta' | 'tiktok' | 'google' | 'adsp'>;
  partnerId?: string;
  dataVisibilityPolicy: 'FULL' | 'DIRECTIONAL' | 'AGENCY_RESTRICTED';
  status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
}
export interface CreateGroupDto {
  businessUnitId: string;
  role:
  | 'campaignLead'
  | 'campaignManager'
  | 'analyst'
  | 'agencyOperator'
  | 'agencyViewer';
  countries: Array< | 'AE'
  | 'AR'
  | 'AU'
  | 'BE'
  | 'BO'
  | 'BR'
  | 'CA'
  | 'CH'
  | 'CL'
  | 'CO'
  | 'CR'
  | 'CZ'
  | 'DE'
  | 'DK'
  | 'DO'
  | 'EC'
  | 'EG'
  | 'ES'
  | 'FI'
  | 'FR'
  | 'GB'
  | 'GT'
  | 'HN'
  | 'HU'
  | 'ID'
  | 'IE'
  | 'IN'
  | 'IT'
  | 'JP'
  | 'MX'
  | 'MY'
  | 'NG'
  | 'NI'
  | 'NL'
  | 'NO'
  | 'NZ'
  | 'PA'
  | 'PE'
  | 'PH'
  | 'PL'
  | 'PR'
  | 'PT'
  | 'PY'
  | 'RO'
  | 'SA'
  | 'SE'
  | 'SG'
  | 'SV'
  | 'TH'
  | 'TR'
  | 'US'
  | 'UY'
  | 'VE'
  | 'ZA'>;
  businessLines: Array< | 'SVOD'
  | 'TVOD'
  | 'CHANNEL'
  | 'Sports SVOD'
  | 'Sports TVOD'
  | 'Sports Channel'>;
  publishers: Array<'meta' | 'tiktok' | 'google' | 'adsp'>;
  partnerId?: string;
  dataVisibilityPolicy: 'FULL' | 'DIRECTIONAL' | 'AGENCY_RESTRICTED';
}
export interface UpdateGroupDto {
  role?:
  | 'campaignLead'
  | 'campaignManager'
  | 'analyst'
  | 'agencyOperator'
  | 'agencyViewer';
  countries?: Array< | 'AE'
  | 'AR'
  | 'AU'
  | 'BE'
  | 'BO'
  | 'BR'
  | 'CA'
  | 'CH'
  | 'CL'
  | 'CO'
  | 'CR'
  | 'CZ'
  | 'DE'
  | 'DK'
  | 'DO'
  | 'EC'
  | 'EG'
  | 'ES'
  | 'FI'
  | 'FR'
  | 'GB'
  | 'GT'
  | 'HN'
  | 'HU'
  | 'ID'
  | 'IE'
  | 'IN'
  | 'IT'
  | 'JP'
  | 'MX'
  | 'MY'
  | 'NG'
  | 'NI'
  | 'NL'
  | 'NO'
  | 'NZ'
  | 'PA'
  | 'PE'
  | 'PH'
  | 'PL'
  | 'PR'
  | 'PT'
  | 'PY'
  | 'RO'
  | 'SA'
  | 'SE'
  | 'SG'
  | 'SV'
  | 'TH'
  | 'TR'
  | 'US'
  | 'UY'
  | 'VE'
  | 'ZA'>;
  businessLines?: Array< | 'SVOD'
  | 'TVOD'
  | 'CHANNEL'
  | 'Sports SVOD'
  | 'Sports TVOD'
  | 'Sports Channel'>;
  publishers?: Array<'meta' | 'tiktok' | 'google' | 'adsp'>;
  partnerId?: string;
  dataVisibilityPolicy?: 'FULL' | 'DIRECTIONAL' | 'AGENCY_RESTRICTED';
}
export interface UpdateGroupStatusDto {
  status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
}
export interface MembershipDto {
  id: string;
  userId: string;
  permissionGroupId: string;
  grantedBy: string;
  expiresAt?: string;
}
export interface CreateMembershipDto {
  userId: string;
  expiresAt?: string;
}
export interface BulkCreateMembershipsDto {
  members: CreateMembershipDto[];
}
export interface UpdateMembershipDto {
  /** Omit for no change; null clears an internal membership expiry; a date sets expiry. */
  expiresAt?: string | null;
}
export interface WorkflowStageDto {
  stage:
  | 'CAMPAIGN_SETUP'
  | 'TITLE_SOURCING'
  | 'ASSET_GEN'
  | 'DATA_PREP'
  | 'ASSET_PUBLISHING'
  | 'CAMPAIGN_SYNC'
  | 'REFTAG_PUBLISH';
  order: number;
  status: 'QUEUED' | 'PENDING' | 'RUNNING' | 'COMPLETE' | 'FAILED';
  retryCount?: number;
  maxRetries?: number;
  retryAfter?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  result?: object | null;
}
export interface WorkflowExecutionPlanDto {
  workflowId: string;
  campaignId: string;
  operationType:
  | 'CAMPAIGN_CREATED'
  | 'PUBLISHER_CAMPAIGN_CONFIG_CHANGED'
  | 'TITLE_CONFIG_CHANGED'
  | 'ASSET_CONFIG_CHANGED'
  | 'PUBLISHER_PRODUCT_CONFIG_CHANGED'
  | 'AD_SPEC_CHANGED'
  | 'TITLE_REMOVED'
  | 'ASSET_REJECTED'
  | 'ASSET_APPROVED'
  | 'ASSET_UPDATED'
  | 'ASSETS_PENDING_REVIEW'
  | 'WORKFLOW_FAILED'
  | 'SCHEDULED_REFRESH';
  priority: 0 | 1 | 2 | 3;
  workflowStatus: 'QUEUED' | 'IN_PROGRESS' | 'COMPLETE' | 'FAILED';
  publisher: string;
  idempotencyKey?: string;
  stages: WorkflowStageDto[];
  createdAt: string;
  updatedAt: string;
}
export type OperationType =
  | 'CAMPAIGN_CREATED'
  | 'PUBLISHER_CAMPAIGN_CONFIG_CHANGED'
  | 'TITLE_CONFIG_CHANGED'
  | 'ASSET_CONFIG_CHANGED'
  | 'PUBLISHER_PRODUCT_CONFIG_CHANGED'
  | 'AD_SPEC_CHANGED'
  | 'TITLE_REMOVED'
  | 'ASSET_REJECTED'
  | 'ASSET_APPROVED'
  | 'ASSET_UPDATED'
  | 'ASSETS_PENDING_REVIEW'
  | 'WORKFLOW_FAILED'
  | 'SCHEDULED_REFRESH';
export interface CampaignTitle {
  gti: string;
  coverImageUrl?: string | null;
  description?: string | null;
  directors?: string[];
  entitlement?: object | null;
  genres?: string[];
  seasonNumber?: number | null;
  starringCast?: string[];
  title?: string | null;
  type?: string | null;
  benefitIds?: string[];
  channels?: string[];
  publicReleaseDate?: number | null;
  liveliness?: string | null;
  predictedStartTime?: string | null;
  predictedEndTime?: string | null;
  inferredTimezone?: string | null;
}
export interface GetCampaignTitlesRequest {
  gtiList?: string[];
  countryCode?: Country;
  locale?: Locale;
  businessLine?: BusinessLine;
  pageId?: string;
  strategyId?: string;
}
export interface RefTagEntityDto {
  entityId?: string;
  entityType?:
  | 'AD'
  | 'AD_GROUP'
  | 'ASSET'
  | 'PRODUCT'
  | 'PRODUCT_SET'
  | 'CAMPAIGN'
  | 'CONCEPT'
  | 'CCG';
  parentEntityId?: string;
  parentEntityType?:
  | 'AD'
  | 'AD_GROUP'
  | 'ASSET'
  | 'PRODUCT'
  | 'PRODUCT_SET'
  | 'CAMPAIGN'
  | 'CONCEPT'
  | 'CCG';
  createdAt?: string;
}
export interface RefTagDto {
  refTag: string;
  programId: string;
  assetId?: string;
  campaignId?: string;
  productSetId?: string;
  productId?: string;
  publisherCampaignId?: string;
  status?: 'OUT_OF_SYNC' | 'SYNCED';
  entities?: RefTagEntityDto[];
}
export interface CreateRefTagDto {
  refTag: string;
  programId: string;
  assetId?: string;
  campaignId?: string;
  productSetId?: string;
  productId?: string;
  publisherCampaignId?: string;
  status?: 'OUT_OF_SYNC' | 'SYNCED';
  entities?: RefTagEntityDto[];
}
export interface CreateRefTagsDto {
  refTags: CreateRefTagDto[];
}
export interface WorkflowProduct {
  assetId: string;
  productSetId?: string;
  productId?: string;
}
export interface WorkflowDto {
  workflowId: string;
  programId: string;
  campaignId: string;
  status:
  | 'QUEUED'
  | 'COMPLETED'
  | 'FAILED'
  | 'IN_PROGRESS'
  | 'ROLLBACK_COMPLETE'
  | 'ROLLBACK_FAILED'
  | 'ROLLBACK_IN_PROGRESS'
  | 'DELETED';
  startTime: string;
  endTime?: string;
  workflowType:
  | 'ASSET_REMOVE'
  | 'PRODUCT_UPDATE'
  | 'PRODUCT_RESTORE'
  | 'CAMPAIGN_CREATE'
  | 'CAMPAIGN_DELETE'
  | 'CAMPAIGN_UPDATE'
  | 'CAMPAIGN_RESTORE';
  publisher: 'meta' | 'tiktok' | 'google' | 'adsp';
  priority: 'IMMEDIATE' | 'NORMAL' | 'SCHEDULED';
  additionalFields?: Map;
  productsAdded?: WorkflowProduct[];
  productsRemoved?: WorkflowProduct[];
}
export interface CreateWorkflowDto {
  programId: string;
  campaignId: string;
  status?:
  | 'QUEUED'
  | 'COMPLETED'
  | 'FAILED'
  | 'IN_PROGRESS'
  | 'ROLLBACK_COMPLETE'
  | 'ROLLBACK_FAILED'
  | 'ROLLBACK_IN_PROGRESS'
  | 'DELETED';
  startTime: string;
  workflowType:
  | 'ASSET_REMOVE'
  | 'PRODUCT_UPDATE'
  | 'PRODUCT_RESTORE'
  | 'CAMPAIGN_CREATE'
  | 'CAMPAIGN_DELETE'
  | 'CAMPAIGN_UPDATE'
  | 'CAMPAIGN_RESTORE';
  publisher: 'meta' | 'tiktok' | 'google' | 'adsp';
  priority: 'IMMEDIATE' | 'NORMAL' | 'SCHEDULED';
  additionalFields?: Map;
  productsAdded?: WorkflowProduct[];
  productsRemoved?: WorkflowProduct[];
}
export interface UpdateWorkflowDto {
  status?:
  | 'QUEUED'
  | 'COMPLETED'
  | 'FAILED'
  | 'IN_PROGRESS'
  | 'ROLLBACK_COMPLETE'
  | 'ROLLBACK_FAILED'
  | 'ROLLBACK_IN_PROGRESS'
  | 'DELETED';
  startTime?: string;
  endTime?: string;
  workflowType?:
  | 'ASSET_REMOVE'
  | 'PRODUCT_UPDATE'
  | 'PRODUCT_RESTORE'
  | 'CAMPAIGN_CREATE'
  | 'CAMPAIGN_DELETE'
  | 'CAMPAIGN_UPDATE'
  | 'CAMPAIGN_RESTORE';
  publisher?: 'meta' | 'tiktok' | 'google' | 'adsp';
  priority?: 'IMMEDIATE' | 'NORMAL' | 'SCHEDULED';
  additionalFields?: Map;
  productsAdded?: WorkflowProduct[];
  productsRemoved?: WorkflowProduct[];
}
export interface TitleLocalizationDto {
  country: string;
  locale: string;
}
export interface TitleContributorsDto {
  actors: string[];
  producers: string[];
  directors: string[];
}
export interface EnrichedTitleDto {
  gti: string;
  productId?: string;
  name: string;
  type: string | null;
  nonLocalizedName?: string;
  contentCategory?: string;
  campaignNames: string[];
  campaignIds: string[];
  productStrategyIds: string[];
  entityType?: string;
  reftag?: string;
  country: string;
  locale: string;
  localizations?: TitleLocalizationDto[];
  businessLine?: string;
  programName?: string;
  contributors?: TitleContributorsDto;
  distributor?: string;
  genres: string[];
  marketingCategorization?: string;
  releaseDate?: string;
  coverImageUrl?: string;
  description?: string;
}
export interface TitlesPaginationDto {
  page: number;
  pageSize: number;
}
export interface TitlesResponseMetaDto {
  pagination: TitlesPaginationDto;
}
export interface PaginatedTitlesResponse {
  data: EnrichedTitleDto[];
  meta: TitlesResponseMetaDto;
  hasMore: boolean;
}
export interface TitleGroup {
  titles: Title[];
  groupKey?: string;
}
export interface GroupTitlesResponse {
  titleGroups: TitleGroup[];
}
export interface GroupTitlesRequest {
  campaignTitles: CampaignTitle[];
  programId: ObjectId;
  campaignId: ObjectId;
  templateConfigId: string;
  groupSize: GroupSize;
  groupingStrategy: 'DEFAULT' | 'ATTRIBUTE';
  groupByAttribute?: 'channels';
  generateAllOrderings?: boolean;
}
export interface GeneratePolarisEmbedUrlResponseDto {
  /** The generated QuickSight embedding URL */
  embedUrl: string;
}
export interface CampaignPlanDto {
  id: string;
  campaignId: string;
  status: string;
  userId: string;
  updatedBy: string;
  createdVia: 'assistant' | 'manual';
  publisherCampaignConfig: object;
  createdAt: string;
  updatedAt: string;
}
export interface CreateCampaignPlanDto {
  /** Campaign ID to create plan for */
  campaignId: string;
  /** Surface that created the plan */
  createdVia?: 'assistant' | 'manual';
}
export interface UpdateCampaignPlanDto {
  /** Publisher campaign configuration. Approval validation rules are documented on each field in GooglePublisherCampaignConfig. */
  publisherCampaignConfig: GooglePublisherCampaignConfig;
}
export interface UpdateStatusDto {
  status: 'APPROVED' | 'CANCELLED';
  /** The updatedAt value of the reviewed plan. A stale value returns HTTP 409. */
  expectedUpdatedAt?: string;
}
export interface GetCatalogGtiResponse {
  gti: string;
  title: string;
  coverImageUrl: string | null;
}
export interface GetCatalogGtiRequest {
  gtiList: string[];
}
export interface SocialSignalsRecommendation {
  signalId: string;
  signalType: string;
  trendText: string;
  signalTimestamp: string;
  confidenceScore: number;
  regionCode: string;
  language: string;
  topics: string;
  entities: string;
  trendSummary: string;
  titleId: string;
  titleName: string;
  titleLanguage: string;
  titleReleaseYear: number;
  titleGenres: string;
  matchType: string;
  embeddingScore: number;
  keywordScore: number;
  evidenceScore: number;
  crossEncoderScore: number;
  matchedKeywords: string;
  llmScore: number;
  llmReason: string;
  relevanceScore: number;
  promotionScore: number;
  trendStrength: number;
  candidateRank: number;
  datasetDate: string;
  riskLabel?: string | null;
  sensitivityFlags?: string | null;
  promotionGuidance?: string | null;
  sentimentLabel?: string | null;
  sentimentScore?: number | null;
  sentimentSummary?: string | null;
  sourcePlatforms?: string | null;
  tiktokPlatformMetadata?: string | null;
  serpapiPlatformMetadata?: string | null;
  trendingHistory?: string | null;
  volumeRaw?: string | null;
  trendCategories?: string | null;
}
export interface PaidMediaInsightsQueryDto {
  view: string;
  sections?: object;
  filters?: object;
  displayOptions?: object;
}
export interface PaidMediaDashboardQueryDto {
  view: string;
  subView: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  summary?: boolean;
  filters?: object;
  displayOptions?: object;
}
export interface PaidMediaDashboardExportDto {
  view: string;
  subView: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: object;
}
export interface MetadataCacheEntryDto {
  /** Summary of an agent turn */
  summary: string;
}
export interface SkillCacheEntryDto {
  /** Skill name */
  name: string;
  /** Skill content */
  content: string;
}
export interface ToolCacheEntryDto {
  /** Tool name */
  name: string;
  /** Cached tool result */
  result: object;
}
export interface AgentCacheDto {
  metadata?: MetadataCacheEntryDto[];
  skills?: SkillCacheEntryDto[];
  tools?: ToolCacheEntryDto[];
}
export interface AgentSessionDto {
  /** MongoDB document ID */
  id: string;
  /** Unique identifier for the agent session */
  sessionId: string;
  /** Human-readable title for the session */
  title?: string;
  /** ID of the entity this session is associated with */
  entityId: string;
  /** Type of entity this session targets */
  entityType: 'campaign';
  /** User who initiated the agent session */
  userId: string;
  /** Cached agent state keyed by agent name */
  agentContext?: Record<string, AgentCacheDto>;
  /** Timestamp when the session was created */
  createdAt: string;
  /** Last modification timestamp */
  lastModifiedAt: string;
}
export interface CreateAgentSessionDto {
  /** Unique identifier for the agent session */
  sessionId: string;
  /** Human-readable title for the session */
  title?: string;
  /** ID of the entity this session is associated with: a campaign ID visible to the caller, or unassigned */
  entityId: string;
  /** Type of entity this session targets */
  entityType: 'campaign';
  /** Session owner. Ignored for ordinary callers, whose own username is always used; admins and service callers may set it to create a session on behalf of a user. */
  userId?: string;
  /** Last modification timestamp; defaults to current time if omitted */
  lastModifiedAt?: string;
  /** Cached agent state keyed by agent name */
  agentContext?: Record<string, AgentCacheDto>;
}
export interface UpdateAgentSessionDto {
  /** Campaign ID for one-time adoption or an idempotent repeat of the current binding; must be supplied with entityType */
  entityId?: string;
  /** Entity type for one-time adoption or an idempotent repeat of the current binding; must be supplied with entityId */
  entityType?: 'campaign';
  /** Human-readable title for the session */
  title?: string;
  /** Updated last modification timestamp */
  lastModifiedAt?: string;
  /** Replacement cached agent state keyed by agent name; null clears the cache */
  agentContext?: Record<string, AgentCacheDto> | null;
}
export type PublisherEntityType =
  | 'CAMPAIGN'
  | 'AD_GROUP'
  | 'AD'
  | 'ASSET'
  | 'PRODUCT_SET'
  | 'CONCEPT'
  | 'CREATIVE';
export type EntityStatus = 'IN_STOCK' | 'OUT_OF_STOCK';
export type SyncStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'SYNCED'
  | 'FAILED'
  | 'SKIPPED';
export interface PublisherEntityDto {
  /** MongoDB publisher-entity row identifier */
  _id: string;
  campaignId: string;
  publisher: Publisher;
  publisherEntityType: PublisherEntityType;
  publisherEntityId?: string;
  publisherEntityResourceName?: string;
  parentPublisherEntityId?: string;
  parentEntityType?: PublisherEntityType;
  parentSourceEntityId?: string;
  productStrategyId?: string;
  adGroupConfigId?: string;
  sourceEntityId?: string;
  entityStatus?: EntityStatus;
  syncStatus?: SyncStatus;
  publisherMetadata: object;
  lastError: string | null;
  createdByWorkflowId: string;
  lastUpdatedByWorkflowId: string;
  createdAt: string;
  updatedAt: string;
}
export interface EvictionResultDto {
  deletedCount: number;
}
export interface AdhocExecutionResponseDto {
  executionArn: string;
}
export interface AdhocEvaluationDto {
  /** The creative's content hash / asset id. */
  assetId: string;
  /** HTTPS URL the creative image is fetched from. */
  url: string;
  /** When true, discards the creative's previous verdicts before starting the run — both the per-row evaluation marks (the store the gate reads to short-circuit) and the cached verdicts — so the evaluation runs fresh instead of returning an already-evaluated result. Omitted or false leaves both stores untouched. */
  force?: boolean;
}
export interface AssetRuleEvaluationDto {
  assetId: string;
  ruleId: string;
  status: 'PASSED' | 'VIOLATED' | 'FAILED';
  reason?: string;
  campaignId?: string;
  evaluatedAt: string;
}
export interface BrandPresetDto {
  brandType: 'PRIME' | 'AMAZON_PRIME' | 'PRIME_VIDEO';
  businessName: string;
  logoS3Key: string;
}
export interface BrandPresetsResponseDto {
  presets: BrandPresetDto[];
}
export interface MetricsDataPointDto {
  label: string;
  value: number;
}
export interface EntityMetricsResponseDto {
  data: MetricsDataPointDto[];
  total: number;
  /** True if results were capped at the max data points limit */
  truncated?: boolean;
}
export interface Aggregation {
  /** Aggregation operation */
  op: 'sum' | 'avg' | 'min' | 'max';
  /** Field to aggregate on (from allowed fields) */
  field: string;
}
export interface Filter {
  /** Field to filter on (from allowed fields) */
  field: string;
  /** Comparison operator */
  op: 'eq' | 'ne' | 'in' | 'gt' | 'gte' | 'lt' | 'lte' | 'exists';
  /** Value to compare against. String for eq/ne, string[] for in, number for gt/gte/lt/lte, boolean for exists. */
  value: object;
}
export interface EntityMetricsQueryDto {
  /** Field path to group by. Omit for a flat total count. */
  groupBy?: string;
  /** Entity to query */
  entity: 'campaigns' | 'assets' | 'workflows';
  /** Time bucket for time series */
  timeBucket?: 'minute' | 'hour' | 'day' | 'week' | 'month';
  /** Aggregation configuration. Omit for count (default). */
  aggregation?: Aggregation;
  /** Array of filter conditions. Each filter specifies a field, operator, and value. For array fields, numeric ops (gt/lt/gte/lte) automatically compare against array length. */
  filters?: Filter[];
  /** Start date (ISO 8601) */
  startDate?: string;
  /** End date (ISO 8601) */
  endDate?: string;
}
export interface DocsListItemDto {
  slug: string;
  app: string;
  folder: string;
  page: string;
  title: string;
  order: number;
}
export interface DocsListResponseDto {
  pages: DocsListItemDto[];
}
export interface DocsSearchHitDto {
  slug: string;
  /** Heading anchor within the page; empty for the text before the first heading. */
  anchor: string;
  pageTitle: string;
  /** Matching section heading; the page title for the text before the first heading. */
  heading: string;
  /** Up to 200 characters of section text around the first match. */
  snippet: string;
}
export interface DocPageResponseDto {
  slug: string;
  title: string;
  /** Page body in markdown, without the page header. */
  markdown: string;
  /** Presigned S3 GET URL keyed by the literal image src used in the markdown. */
  images: Record<string, string>;
  /** ISO-8601 time at which the image URLs expire. */
  expiresAt: string;
}
export interface IssueReportResponseDto {
  status: 'CREATED';
  attachmentStatus: 'UPLOADED' | 'FAILED' | 'NONE';
  /** Returned to internal callers only */
  ticketId?: string;
  /** Returned to internal callers only */
  ticketUrl?: string;
}
export interface IssueReportErrorBody {
  code:
  | 'FEATURE_DISABLED'
  | 'RATE_LIMITED'
  | 'TICKETING_UNAVAILABLE'
  | 'FORBIDDEN_CALLER';
  message: string;
}
export type IssueType =
  | 'ACCESS_ISSUE'
  | 'WORKFLOW_FAILURE'
  | 'CAMPAIGN_SETUP_ISSUE'
  | 'MISSING_CREATIVES'
  | 'REFTAG_ISSUE'
  | 'CREATIVE_BRANDING_ISSUE'
  | 'OTHER';
export interface IssueReportAttachmentDto {
  /** File name shown on the ticket */
  filename: string;
  /** MIME type of the file, for example image/png */
  contentType: string;
  /** uploadKey returned by POST /issue-reports/attachment-uploads after the file was PUT to its url */
  uploadKey: string;
}
export interface CreateIssueReportDto {
  /** Team the reporter belongs to */
  team: string;
  issueType: IssueType;
  /** Short name of the issue type; required when issueType is OTHER and ignored otherwise */
  otherIssueType?: string;
  problemDescription: string;
  expectedBehavior?: string;
  actualBehavior?: string;
  businessImpact?: string;
  affectedCampaignCount?: number;
  /** HTTPS links to affected campaigns */
  exampleCampaignLinks?: string[];
  attachment?: IssueReportAttachmentDto;
}
